import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from './ui/dropdown-menu';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './ui/command';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { ControlRowActions } from './ControlRowShared';
import BulkImportControlsDialog from './BulkImportControlsDialog';
import { TruncatedTooltip } from './TruncatedTooltip';
import { toast } from 'sonner';
import { formatDate } from '../utils/dateFormatter';
import { formatStatus } from '../utils/formatStatus';
import { AutoEvidenceBadge } from './AutoEvidenceBadge';
import ControlDetail from './ControlDetail';
import NaReasonDialog from './NaReasonDialog';
import ManageTechnologiesDialog from './ManageTechnologiesDialog';
import ManageFrameworksDialog from './ManageFrameworksDialog';
import * as XLSX from 'xlsx';
import { 
  ArrowLeft, 
  Shield, 
  Filter, 
  Search, 
  CheckCircle, 
  Clock, 
  AlertCircle,
  ExternalLink,
  Calendar,
  Building,
  BarChart3,
  FileText,
  Users,
  Settings,
  Crown,
  LogOut,
  RefreshCw,
  Plus,
  ChevronDown,
  ChevronRight,
  FolderOpen,
  Sparkles,
  Zap,
  Trash2,
  Undo2,
  AlertTriangle,
  Loader2,
  TrendingDown,
  Check,
  X,
  Lightbulb,
  Server,
  ShieldCheck,
  Cpu,
  Link2,
  Download,
  ChevronsUpDown,
  CheckCircle2,
  XCircle,
  MinusCircle,
  SlidersHorizontal,
  MoreHorizontal,
  Settings2,
  Building2,
  Upload
} from 'lucide-react';

// Using relative URLs
const API = '/api';

/**
 * Compact "X of Y Evidences" indicator shown under the status pill.
 * Colors:
 *   green if X >= Y > 0 (complete)
 *   amber if 0 < X < Y (partial)
 *   slate if X = 0 or no requirements
 */
const EvidenceProgress = ({ control, compact = false, uniformWidth = false }) => {
  if (control?.is_deleted) return null;
  const required = control?.evidence_required_count;
  if (required === null || required === undefined) return null;
  const uploaded = control?.evidence_uploaded_count ?? 0;
  if (required === 0) {
    return (
      <span
        className={`text-[11px] text-slate-400 ${compact ? '' : 'mt-1 block'}`}
        data-testid={`evidence-progress-${control.id}`}
      >
        No evidence required
      </span>
    );
  }
  const tone =
    uploaded >= required
      ? 'text-emerald-700 bg-emerald-50 border-emerald-200'
      : uploaded > 0
      ? 'text-amber-700 bg-amber-50 border-amber-200'
      : 'text-slate-500 bg-slate-50 border-slate-200';
  // uniformWidth: renders as a 128px block-level pill so it aligns visually
  // with the w-32 Status Select trigger stacked directly above it.
  const shape = uniformWidth
    ? 'flex w-28 h-8 items-center justify-center'
    : `inline-flex items-center ${compact ? '' : 'mt-1'}`;
  return (
    <span
      className={`text-[11px] px-1.5 py-0.5 rounded border ${tone} ${shape}`}
      data-testid={`evidence-progress-${control.id}`}
      title={`${uploaded} of ${required} evidence requirement${required === 1 ? '' : 's'} uploaded`}
    >
      {uploaded} of {required} Evidence{required === 1 ? '' : 's'}
    </span>
  );
};

const AuditDetail = () => {
  const { auditId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { returnToAdmin, user, currentWorkspace } = useAuth();
  
  // Helper function to get correct dashboard URL based on workspace role
  const getDashboardUrl = () => {
    if (!user || !currentWorkspace) return '/';
    if (currentWorkspace.role === 'super_admin') return '/admin';
    if (currentWorkspace.role === 'auditor') return '/auditor/dashboard';
    if (currentWorkspace.role === 'employee') return '/employee/portal';
    return '/dashboard';
  };
  const [audit, setAudit] = useState(null);
  const [controls, setControls] = useState([]);
  const [deletedControls, setDeletedControls] = useState([]);
  const [filteredControls, setFilteredControls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  // Debounced search term used for server-side fetching of controls
  const [debouncedSearch, setDebouncedSearch] = useState('');
  // Server-side search results — null when no search active
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [selectedFramework, setSelectedFramework] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [overlapFilter, setOverlapFilter] = useState('all');
  const [selectedTechnology, setSelectedTechnology] = useState('all');
  const [autoEvidenceFilter, setAutoEvidenceFilter] = useState('all');
  const [ownerFilter, setOwnerFilter] = useState('all'); // 'all' | 'mine' | 'unassigned' | 'needs_reassignment'
  const [techFilterOpen, setTechFilterOpen] = useState(false);
  const [viewMode, setViewMode] = useState('table'); // 'category', 'table', or 'technology'
  // Toggle for the "Deleted / Retired" view — sits alongside the view-mode
  // pills. When on, the same view mode (Table / Categories / Technologies)
  // renders `deletedControls` instead of `controls`, and all filter chips
  // (framework, technology, category, etc.) still apply.
  const [showingDeleted, setShowingDeleted] = useState(false);
  const [openCategories, setOpenCategories] = useState({});
  const [openTechnologies, setOpenTechnologies] = useState({}); // For technology view expansion
  const [modalControlId, setModalControlId] = useState(null); // Control detail modal
  const [technologyMap, setTechnologyMap] = useState({}); // Map of tech ID -> tech name
  const [allTechnologies, setAllTechnologies] = useState([]); // Full catalog (categories with technologies)
  const [showManageTechDialog, setShowManageTechDialog] = useState(false);
  const [showManageFwDialog, setShowManageFwDialog] = useState(false);
  
  // Dialog states for new features
  const [showRegenerateDialog, setShowRegenerateDialog] = useState(false);
  const [showAddControlDialog, setShowAddControlDialog] = useState(false);
  const [showBulkImportDialog, setShowBulkImportDialog] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [addingControl, setAddingControl] = useState(false);
  const [deduplicating, setDeduplicating] = useState(false);
  const [evaluatingEvidence, setEvaluatingEvidence] = useState(false);
  const [dedupElapsed, setDedupElapsed] = useState(0);
  // Progress tracking for category-by-category regeneration
  const [regenerationPromptVersions, setRegenerationPromptVersions] = useState(null);
  const [regenerationProgress, setRegenerationProgress] = useState({
    phase: 'idle', // 'idle', 'planning', 'generating', 'finalizing', 'deduplicating', 'complete', 'error'
    plan: null,
    categories: [],
    currentCategory: null,
    currentCategoryIndex: 0,
    totalCategories: 0,
    processedCategories: [],
    categoryResults: {},
    totalAdded: 0,
    totalUpdated: 0,
    dedupElapsed: 0
  });
  
  // Optimize with AI state
  const [showOptimizeDialog, setShowOptimizeDialog] = useState(false);
  const [optimizing, setOptimizing] = useState(false);
  const [optimizationResult, setOptimizationResult] = useState(null);
  const [applyingOptimization, setApplyingOptimization] = useState(false);
  const [selectedForDeletion, setSelectedForDeletion] = useState(new Set());
  
  // Link Policies state
  const [showPolicyDialog, setShowPolicyDialog] = useState(false);
  const [policyStatus, setPolicyStatus] = useState(null);
  const [linkingPolicies, setLinkingPolicies] = useState(false);
  const [policyLinkProgress, setPolicyLinkProgress] = useState({ current: 0, total: 0, results: [] });
  const [policyTab, setPolicyTab] = useState(null); // 'linked' | 'needs' | 'none' | null

  // Progress tracking for category-by-category optimization
  const [optimizationProgress, setOptimizationProgress] = useState({
    phase: 'idle', // 'idle', 'loading_categories', 'analyzing', 'complete', 'error'
    categories: [],
    currentCategory: null,
    currentCategoryIndex: 0,
    totalCategories: 0,
    processedCategories: [],
    categoryResults: {} // { categoryName: { keep: [], delete: [], summary: '' } }
  });
  
  // Form state for manual control
  const [controlForm, setControlForm] = useState({
    framework: '',
    control_name: '',
    description: '',
    category: '',
    evidence_required: '',
    type: 'manual'
  });
  
  // Deleted controls state
  const [showDeletedControls, setShowDeletedControls] = useState(false);
  const [restoringControlId, setRestoringControlId] = useState(null);
  
  // Delete confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [controlToDelete, setControlToDelete] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  
  // Stat card filter state - when user clicks a stat card
  const [activeStatFilter, setActiveStatFilter] = useState(null); // null, 'total', 'completed', 'pending', 'not_applicable', 'automated', 'manual', 'deleted'
  
  // Check if user is emulated (super admin viewing as customer)
  const isEmulated = localStorage.getItem('emulated_user') !== null;

  // Group controls by category (using applicable_categories for multi-category controls)
  const controlsByCategory = useMemo(() => {
    const grouped = {};
    filteredControls.forEach(control => {
      const categories = control.applicable_categories && control.applicable_categories.length > 0
        ? control.applicable_categories
        : [control.category || 'Uncategorized'];
      categories.forEach(category => {
        if (!grouped[category]) {
          grouped[category] = [];
        }
        grouped[category].push(control);
      });
    });
    // Sort categories alphabetically
    return Object.keys(grouped).sort().reduce((acc, key) => {
      acc[key] = grouped[key];
      return acc;
    }, {});
  }, [filteredControls]);

  // Auto-evidence summary counts (reactive to active filters)
  const autoEvidenceCounts = useMemo(() => {
    const c = { satisfied: 0, needMore: 0, violations: 0, na: 0 };
    filteredControls.forEach(ctrl => {
      const ae = ctrl.auto_evidence;
      if (!ae || !ae.status) return;
      if (ae.status === 'pass') c.satisfied++;
      else if (ae.status === 'insufficient_evidence' || ae.status === 'partial') c.needMore++;
      else if (ae.status === 'fail') c.violations++;
      else if (ae.status === 'not_applicable') c.na++;
    });
    return c;
  }, [filteredControls]);

  // Controls grouped by category (reactive to active filters)
  const categoryBreakdown = useMemo(() => {
    const grouped = {};
    filteredControls.forEach(control => {
      const cats = control.applicable_categories && control.applicable_categories.length > 0
        ? control.applicable_categories
        : [control.category || 'Uncategorized'];
      cats.forEach(cat => { grouped[cat] = (grouped[cat] || 0) + 1; });
    });
    return Object.entries(grouped).sort((a, b) => b[1] - a[1]).slice(0, 7);
  }, [filteredControls]);

  // Distinct list of employees who own at least one control in this audit.
  // Powers the dynamic per-employee pills on the Owner filter row so admins
  // can slice by any assigned team member without a separate dropdown.
  const assignedOwners = useMemo(() => {
    const map = new Map();
    (controls || []).forEach((c) => {
      const rid = c.reviewer_id;
      if (!rid || map.has(rid)) return;
      map.set(rid, {
        id: rid,
        name: c.reviewer_name || (c.reviewer_email ? c.reviewer_email.split('@')[0] : 'Owner'),
        email: c.reviewer_email || '',
      });
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [controls]);

  // Filter toolbar helpers
  const hasActiveFilters = searchTerm || selectedFramework !== 'all' || selectedType !== 'all' || selectedStatus !== 'all' || overlapFilter !== 'all' || selectedTechnology !== 'all' || autoEvidenceFilter !== 'all';
  const activeFilterCount = [selectedFramework, selectedType, selectedStatus, selectedTechnology, overlapFilter, autoEvidenceFilter].filter(v => v !== 'all').length;
  const resetAllFilters = () => {
    setSearchTerm('');
    setSelectedFramework('all');
    setSelectedType('all');
    setSelectedStatus('all');
    setOverlapFilter('all');
    setSelectedTechnology('all');
    setAutoEvidenceFilter('all');
    setActiveStatFilter(null);
    setShowDeletedControls(false);
  };

  // Renders a horizontal group of selectable filter pills
  // Small red pill shown beside a control's ref whenever the control
  // is soft-deleted — whether via a framework/tech retire operation
  // OR via the per-row trash icon. Communicates "this is archived" at
  // a glance in the table.
  const renderDeletedBadge = (control) => {
    if (!control?.is_deleted) return null;
    return (
      <span
        className="text-[10px] font-semibold uppercase tracking-wide text-rose-700 bg-rose-50 border border-rose-200 px-1.5 py-0.5 rounded flex-shrink-0"
        title={control.retirement_reason || 'Deleted'}
        data-testid={`deleted-badge-${control.id}`}
      >
        Deleted
      </span>
    );
  };

  const renderPills = (options, value, onChange, testid, variant = 'default') => {
    // Styling is now per-option. Options can carry their own `variant`
    // ('rose' for retired items) which overrides the row-level default.
    // This lets us paint SOC 2 / GDPR / GitHub red in the Deleted view
    // while leaving the neutral "All" / "No technology" scaffolding pills
    // in the standard blue palette.
    const rowIsRose = variant === 'rose';
    return (
      <div className="flex flex-wrap items-center gap-1.5" data-testid={testid}>
        {options.map((opt) => {
          const optIsRose = (opt.variant || (rowIsRose && opt.value !== 'all' && opt.value !== '__none__' ? 'rose' : 'default')) === 'rose';
          const activeCls = optIsRose
            ? 'bg-rose-600 text-white border-rose-600'
            : 'bg-[#0F62FE] text-white border-[#0F62FE]';
          const idleCls = optIsRose
            ? 'bg-white text-rose-700 border-rose-200 hover:bg-rose-50'
            : 'bg-white text-[#475569] border-[#D7E0F2] hover:bg-[#F1F5F9]';
          return (
            <button
              key={opt.value}
              onClick={() => onChange(opt.value)}
              data-testid={`${testid}-${opt.value}`}
              className={`text-xs px-3 py-1 rounded-full border transition-colors ${
                value === opt.value ? activeCls : idleCls
              }`}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    );
  };


  // Toggle category open/closed
  const toggleCategory = (category) => {
    setOpenCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  // Expand all categories
  const expandAllCategories = () => {
    const allOpen = {};
    Object.keys(controlsByCategory).forEach(cat => {
      allOpen[cat] = true;
    });
    setOpenCategories(allOpen);
  };

  // Collapse all categories
  const collapseAllCategories = () => {
    setOpenCategories({});
  };

  useEffect(() => {
    fetchAuditData();
  }, [auditId]);

  // Deep-link support (Feb 2026): when AuditDetail is opened with
  // `?control_id=<id>` in the URL (e.g. from the Linked-Controls chooser
  // on the GW Security Posture page), auto-open the control modal so
  // the user lands directly on the control they wanted to inspect.
  // We strip the query param after opening to keep the URL clean.
  useEffect(() => {
    const target = searchParams.get('control_id');
    if (!target || !auditId || loading) return;
    // Only auto-open when the control actually exists in this audit's
    // current list — otherwise just leave the param in place (it may
    // become valid after a re-fetch finishes).
    const exists =
      (controls || []).some((c) => c.id === target) ||
      (deletedControls || []).some((c) => c.id === target);
    if (exists) {
      setModalControlId(target);
      const next = new URLSearchParams(searchParams);
      next.delete('control_id');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, auditId, loading, controls, deletedControls, setSearchParams]);

  // Debounce searchTerm for server-side fetching
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 350);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Server-side search: re-fetch controls + deleted-controls when debounced search changes
  useEffect(() => {
    if (!auditId) return;
    if (!debouncedSearch) {
      setSearchResults(null);
      setSearching(false);
      return;
    }
    let cancelled = false;
    setSearching(true);
    (async () => {
      try {
        const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
        const [r1, r2] = await Promise.all([
          axios.get(`${API}/audits/${auditId}/controls`, {
            headers,
            params: { search: debouncedSearch },
          }),
          axios.get(`${API}/audits/${auditId}/deleted-controls`, {
            headers,
            params: { search: debouncedSearch },
          }).catch(() => ({ data: [] })),
        ]);
        if (!cancelled) {
          setSearchResults({ active: r1.data || [], deleted: r2.data || [] });
        }
      } catch (err) {
        console.error('Server-side search failed:', err);
        if (!cancelled) setSearchResults({ active: [], deleted: [] });
      } finally {
        if (!cancelled) setSearching(false);
      }
    })();
    return () => { cancelled = true; };
  }, [debouncedSearch, auditId]);

  useEffect(() => {
    filterControls();
  }, [controls, deletedControls, searchResults, selectedFramework, selectedType, selectedStatus, overlapFilter, selectedTechnology, autoEvidenceFilter, activeStatFilter, ownerFilter, user?.id, showingDeleted]);

  const fetchAuditData = async () => {
    try {
      const [auditResponse, controlsResponse, deletedResponse, techResponse] = await Promise.all([
        axios.get(`${API}/audits/${auditId}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }),
        axios.get(`${API}/audits/${auditId}/controls`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }),
        axios.get(`${API}/audits/${auditId}/deleted-controls`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }).catch(() => ({ data: [] })), // Handle gracefully if endpoint doesn't exist yet
        axios.get(`${API}/technologies/all`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }).catch(() => ({ data: [] })) // Handle gracefully if no technologies
      ]);
      
      setAudit(auditResponse.data);
      setControls(controlsResponse.data);
      setDeletedControls(deletedResponse.data || []);
      
      // Build technology name map
      const techMap = {};
      (techResponse.data || []).forEach(category => {
        (category.technologies || []).forEach(tech => {
          techMap[tech.id] = { name: tech.name, category: category.name, emoji: category.emoji };
        });
      });
      setTechnologyMap(techMap);
      setAllTechnologies(techResponse.data || []);
    } catch (error) {
      console.error('Failed to fetch audit data:', error);
      
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('Access denied. You do not have permission to view this audit.');
      } else if (error.response?.status === 404) {
        toast.error('Audit not found.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to fetch audit data. Please try again.');
      }
      
      // Don't redirect - stay on the page so user can navigate back properly
      setAudit(null);
      setControls([]);
      setDeletedControls([]);
    } finally {
      setLoading(false);
    }
  };

  const restoreControl = async (controlId) => {
    setRestoringControlId(controlId);
    try {
      await axios.put(`${API}/controls/${controlId}/restore`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('Control restored successfully');
      await fetchAuditData(); // Refresh data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore control');
    } finally {
      setRestoringControlId(null);
    }
  };

  const openDeleteConfirm = (control) => {
    setControlToDelete(control);
    setDeleteConfirmText('');
    setShowDeleteConfirm(true);
  };

  const handleDeleteControl = async () => {
    if (deleteConfirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }
    
    setDeleting(true);
    try {
      await axios.delete(`${API}/controls/${controlToDelete.id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('Control deleted successfully');
      setShowDeleteConfirm(false);
      setControlToDelete(null);
      setDeleteConfirmText('');
      await fetchAuditData(); // Refresh data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete control');
      console.error('Delete error:', error);
    } finally {
      setDeleting(false);
    }
  };

  const filterControls = () => {
    // Server-side search results take priority over the full lists
    const baseActive = searchResults ? searchResults.active : controls;
    // Delete view: every soft-deleted control on the audit — both
    // retire-flow archives and individual per-control deletes.
    const baseDeleted = searchResults ? searchResults.deleted : deletedControls;

    // "Deleted / Retired" view: use the deleted controls as the base but
    // still let framework / technology / category / status filters and the
    // active stat card refine the list.
    let filtered = showingDeleted ? [...baseDeleted] : [...baseActive];

    // Legacy: stat card "Deleted" toggle keeps working as a shortcut into
    // the deleted list even when the pill isn't switched.
    if (!showingDeleted && activeStatFilter === 'deleted') {
      setFilteredControls([...baseDeleted]);
      return;
    }

    // Apply stat card filter first (only meaningful for active view)
    if (!showingDeleted && activeStatFilter) {
      switch (activeStatFilter) {
        case 'completed':
          filtered = filtered.filter(control => control.status === 'completed');
          break;
        case 'pending':
          filtered = filtered.filter(control => control.status === 'pending');
          break;
        case 'not_applicable':
          filtered = filtered.filter(control => control.status === 'not_applicable');
          break;
        case 'automated':
          filtered = filtered.filter(control => control.type === 'automated');
          break;
        case 'manual':
          filtered = filtered.filter(control => control.type === 'manual');
          break;
        case 'total':
        default:
          // Show all controls
          break;
      }
    }

    // Note: text search is handled server-side via the `search` param.

    // Framework filter — check both applicable_frameworks array and framework string
    if (selectedFramework !== 'all') {
      filtered = filtered.filter(control => {
        if (control.applicable_frameworks && control.applicable_frameworks.length > 0) {
          return control.applicable_frameworks.includes(selectedFramework);
        }
        return control.framework === selectedFramework;
      });
    }

    // Type filter (only apply if no stat card filter for type)
    if (selectedType !== 'all' && activeStatFilter !== 'automated' && activeStatFilter !== 'manual') {
      filtered = filtered.filter(control => control.type === selectedType);
    }

    // Status filter (only apply if no stat card filter for status)
    if (selectedStatus !== 'all' && !['completed', 'pending', 'not_applicable'].includes(activeStatFilter)) {
      filtered = filtered.filter(control => control.status === selectedStatus);
    }

    // Overlap filter
    if (overlapFilter !== 'all') {
      filtered = filtered.filter(control => {
        const fwCount = (control.applicable_frameworks && control.applicable_frameworks.length) || 1;
        const catCount = (control.applicable_categories && control.applicable_categories.length) || 1;
        if (overlapFilter === 'multi_framework') return fwCount > 1;
        if (overlapFilter === 'multi_category') return catCount > 1;
        if (overlapFilter === 'overlapping') return fwCount > 1 || catCount > 1;
        return true;
      });
    }

    // Technology filter
    if (selectedTechnology !== 'all') {
      filtered = filtered.filter(control => {
        const techIds = control.technology_ids || [];
        if (selectedTechnology === '__none__') {
          return techIds.length === 0;
        }
        return techIds.includes(selectedTechnology);
      });
    }

    // Auto-Evidence filter — predicates mirror the AutoEvidenceBadge rendering:
    //   has_any         → automated + verdict present
    //   manual          → control.type !== 'automated'
    //   not_evaluated   → automated + no verdict
    //   <status>        → automated + verdict matches the literal status string
    if (autoEvidenceFilter !== 'all') {
      filtered = filtered.filter(control => {
        const ae = control.auto_evidence;
        const isAutomated = control.type === 'automated';
        const hasVerdict = isAutomated && ae && !!ae.status;
        if (autoEvidenceFilter === 'has_any') return hasVerdict;
        if (autoEvidenceFilter === 'manual') return !isAutomated;
        if (autoEvidenceFilter === 'not_evaluated') return isAutomated && !hasVerdict;
        return hasVerdict && ae.status === autoEvidenceFilter;
      });
    }

    // Owner filter — "mine" (current user), "unassigned", or a specific
    // employee (encoded as "user:<reviewer_id>") who owns at least one
    // control in this audit. The dynamic per-owner pills are computed from
    // the controls' reviewer_id values.
    if (ownerFilter === 'mine' && user?.id) {
      filtered = filtered.filter(c => c.reviewer_id === user.id);
    } else if (ownerFilter === 'unassigned') {
      filtered = filtered.filter(c => !c.reviewer_id);
    } else if (typeof ownerFilter === 'string' && ownerFilter.startsWith('user:')) {
      const uid = ownerFilter.slice(5);
      filtered = filtered.filter(c => c.reviewer_id === uid);
    }

    setFilteredControls(filtered);
  };

  // Handle stat card click
  const handleStatCardClick = (filterType) => {
    if (activeStatFilter === filterType) {
      // If clicking the same filter, clear it
      setActiveStatFilter(null);
      setShowDeletedControls(false);
    } else {
      setActiveStatFilter(filterType);
      setShowDeletedControls(filterType === 'deleted');
    }
  };

  // Regenerate with AI handlers - Category by category approach
  const handleRegenerateControls = async () => {
    setRegenerating(true);
    setRegenerationProgress({
      phase: 'planning',
      plan: null,
      categories: [],
      currentCategory: null,
      currentCategoryIndex: 0,
      totalCategories: 0,
      processedCategories: [],
      categoryResults: {},
      totalAdded: 0,
      totalUpdated: 0
    });
    
    try {
      // Step 1: Get regeneration plan from AI
      const planResponse = await axios.post(
        `${API}/audits/${auditId}/regeneration-plan`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      
      if (!planResponse.data.success) {
        throw new Error('Failed to create regeneration plan');
      }
      
      const plan = planResponse.data.plan;
      const categories = plan.categories || [];
      
      // Capture prompt versions used for this regeneration
      if (planResponse.data.prompt_versions) {
        setRegenerationPromptVersions(planResponse.data.prompt_versions);
      }
      
      if (categories.length === 0) {
        toast.info('No categories to regenerate');
        setRegenerating(false);
        setShowRegenerateDialog(false);
        return;
      }
      
      setRegenerationProgress(prev => ({
        ...prev,
        phase: 'generating',
        plan: plan,
        categories: categories,
        totalCategories: categories.length
      }));
      
      // Step 2: Process each category sequentially
      let totalAdded = 0;
      let totalUpdated = 0;
      const categoryResults = {};
      
      for (let i = 0; i < categories.length; i++) {
        const category = categories[i];
        
        setRegenerationProgress(prev => ({
          ...prev,
          currentCategory: category.name,
          currentCategoryIndex: i
        }));
        
        try {
          const response = await axios.post(
            `${API}/audits/${auditId}/regenerate-category`,
            {
              category: category.name,
              framework: category.framework,
              description: category.description
            },
            { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
          );
          
          if (response.data.success) {
            totalAdded += response.data.added_count || 0;
            totalUpdated += response.data.updated_count || 0;
            categoryResults[category.name] = {
              added: response.data.added_count || 0,
              updated: response.data.updated_count || 0,
              controls: response.data.controls_generated || 0,
              success: true
            };
          } else {
            categoryResults[category.name] = {
              added: 0,
              updated: 0,
              controls: 0,
              success: false,
              error: response.data.error
            };
          }
        } catch (categoryError) {
          console.error(`Error regenerating category ${category.name}:`, categoryError);
          categoryResults[category.name] = {
            added: 0,
            updated: 0,
            controls: 0,
            success: false,
            error: categoryError.message
          };
        }
        
        setRegenerationProgress(prev => ({
          ...prev,
          processedCategories: [...prev.processedCategories, category.name],
          categoryResults: { ...prev.categoryResults, ...categoryResults },
          totalAdded: totalAdded,
          totalUpdated: totalUpdated
        }));
      }
      
      // Step 3: Finalize regeneration
      setRegenerationProgress(prev => ({ ...prev, phase: 'finalizing' }));
      
      await axios.post(
        `${API}/audits/${auditId}/finalize-regeneration`,
        {
          categories_processed: categories.length,
          total_added: totalAdded,
          total_updated: totalUpdated
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      
      // Step 4: Deduplicate controls
      setRegenerationProgress(prev => ({ ...prev, phase: 'deduplicating', dedupElapsed: 0 }));
      const dedupTimer = setInterval(() => {
        setRegenerationProgress(prev => ({ ...prev, dedupElapsed: prev.dedupElapsed + 1 }));
      }, 1000);
      try {
        const dedupResponse = await axios.post(
          `${API}/audits/${auditId}/deduplicate-controls`,
          {},
          { 
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            timeout: 300000
          }
        );
        clearInterval(dedupTimer);
        const { merged_count, pass1_merged, pass2_merged, elapsed_seconds } = dedupResponse.data || {};
        setRegenerationProgress(prev => ({ ...prev, dedupElapsed: Math.round(elapsed_seconds || prev.dedupElapsed) }));
        if (merged_count > 0) {
          toast.success(`Deduplicated ${merged_count} controls (${pass1_merged || 0} within-framework, ${pass2_merged || 0} cross-framework) in ${elapsed_seconds}s`);
        }
      } catch (dedupErr) {
        clearInterval(dedupTimer);
        console.error('Post-regeneration dedup error (non-fatal):', dedupErr);
      }
      
      setRegenerationProgress(prev => ({ ...prev, phase: 'complete' }));
      
      toast.success(`Regeneration complete: ${totalAdded} controls added, ${totalUpdated} updated`);
      
      // Refresh controls data but keep dialog open for user to see summary
      await fetchAuditData();
      
    } catch (error) {
      console.error('Regeneration error:', error);
      setRegenerationProgress(prev => ({ ...prev, phase: 'error' }));
      toast.error(error.response?.data?.detail || 'Failed to regenerate controls');
    } finally {
      setRegenerating(false);
    }
  };

  const closeRegenerateDialog = () => {
    if (!regenerating) {
      setShowRegenerateDialog(false);
      setRegenerationPromptVersions(null);
      setRegenerationProgress({
        phase: 'idle',
        plan: null,
        categories: [],
        currentCategory: null,
        currentCategoryIndex: 0,
        totalCategories: 0,
        processedCategories: [],
        categoryResults: {},
        totalAdded: 0,
        totalUpdated: 0
      });
    }
  };

  // Optimize with AI handlers - Category by category approach
  const handleOptimizeWithAI = async () => {
    setOptimizing(true);
    setOptimizationResult(null);
    setOptimizationProgress({
      phase: 'loading_categories',
      categories: [],
      currentCategory: null,
      currentCategoryIndex: 0,
      totalCategories: 0,
      processedCategories: [],
      categoryResults: {}
    });
    
    try {
      // Step 1: Get all categories for this audit
      const categoriesResponse = await axios.get(
        `${API}/audits/${auditId}/optimization-categories`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      
      if (!categoriesResponse.data.success) {
        throw new Error('Failed to load categories');
      }
      
      const categories = categoriesResponse.data.categories;
      const allCategoryNames = categories.map(c => c.name);
      
      if (categories.length === 0) {
        setOptimizationProgress(prev => ({ ...prev, phase: 'complete' }));
        setOptimizationResult({
          success: true,
          analysis_summary: "No controls found to optimize",
          keep: [],
          delete: [],
          total_controls: 0,
          keep_count: 0,
          delete_count: 0,
          recommendations: []
        });
        setOptimizing(false);
        return;
      }
      
      setOptimizationProgress(prev => ({
        ...prev,
        phase: 'analyzing',
        categories: categories,
        totalCategories: categories.length
      }));
      
      // Step 2: Process each category sequentially
      const allKeep = [];
      const allDelete = [];
      const allSummaries = [];
      const categoryResults = {};
      
      for (let i = 0; i < categories.length; i++) {
        const category = categories[i];
        
        setOptimizationProgress(prev => ({
          ...prev,
          currentCategory: category.name,
          currentCategoryIndex: i
        }));
        
        try {
          const response = await axios.post(
            `${API}/audits/${auditId}/optimize-category`,
            { 
              category: category.name,
              all_categories: allCategoryNames
            },
            { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
          );
          
          if (response.data.success) {
            allKeep.push(...(response.data.keep || []));
            allDelete.push(...(response.data.delete || []));
            if (response.data.analysis_summary) {
              allSummaries.push(`${category.name}: ${response.data.analysis_summary}`);
            }
            categoryResults[category.name] = {
              keep: response.data.keep || [],
              delete: response.data.delete || [],
              summary: response.data.analysis_summary || ''
            };
          }
        } catch (categoryError) {
          console.error(`Error analyzing category ${category.name}:`, categoryError);
          // Continue with other categories even if one fails
          categoryResults[category.name] = {
            keep: [],
            delete: [],
            summary: `Error analyzing: ${categoryError.message}`,
            error: true
          };
        }
        
        setOptimizationProgress(prev => ({
          ...prev,
          processedCategories: [...prev.processedCategories, category.name],
          categoryResults: { ...prev.categoryResults, ...categoryResults }
        }));
      }
      
      // Step 3: Aggregate results
      const totalControls = allKeep.length + allDelete.length;
      const deletePercentage = totalControls > 0 
        ? Math.round((allDelete.length / totalControls) * 100)
        : 0;
      
      const aggregatedResult = {
        success: true,
        analysis_summary: `Analyzed ${categories.length} categories with ${totalControls} controls. Recommending ${allDelete.length} controls for deletion (${deletePercentage}% reduction).`,
        optimization_score: `${deletePercentage}%`,
        keep: allKeep,
        delete: allDelete,
        total_controls: totalControls,
        keep_count: allKeep.length,
        delete_count: allDelete.length,
        recommendations: allSummaries.slice(0, 5), // Top 5 category summaries
        category_breakdown: categoryResults
      };
      
      setOptimizationResult(aggregatedResult);
      setOptimizationProgress(prev => ({ ...prev, phase: 'complete' }));
      
      // Pre-select all AI-recommended deletions
      const deletionIds = new Set(allDelete.map(c => c.id));
      setSelectedForDeletion(deletionIds);
      
    } catch (error) {
      console.error('Optimization error:', error);
      setOptimizationProgress(prev => ({ ...prev, phase: 'error' }));
      toast.error(error.response?.data?.detail || 'Failed to optimize controls with AI');
    } finally {
      setOptimizing(false);
    }
  };

  const toggleControlSelection = (controlId) => {
    setSelectedForDeletion(prev => {
      const newSet = new Set(prev);
      if (newSet.has(controlId)) {
        newSet.delete(controlId);
      } else {
        newSet.add(controlId);
      }
      return newSet;
    });
  };

  const selectAllForDeletion = () => {
    if (optimizationResult?.delete) {
      setSelectedForDeletion(new Set(optimizationResult.delete.map(c => c.id)));
    }
  };

  const deselectAllForDeletion = () => {
    setSelectedForDeletion(new Set());
  };

  const handleApplyOptimization = async () => {
    if (selectedForDeletion.size === 0) {
      toast.error('Please select at least one control to delete');
      return;
    }
    
    setApplyingOptimization(true);
    try {
      const response = await axios.post(
        `${API}/audits/${auditId}/apply-optimization`,
        { control_ids: Array.from(selectedForDeletion) },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );
      
      if (response.data.success) {
        toast.success(response.data.message || `Successfully deleted ${response.data.deleted_count} controls`);
        setShowOptimizeDialog(false);
        setOptimizationResult(null);
        setSelectedForDeletion(new Set());
        await fetchAuditData(); // Refresh data
      } else {
        toast.error(response.data.message || 'Failed to apply optimization');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply optimization');
      console.error(error);
    } finally {
      setApplyingOptimization(false);
    }
  };

  const closeOptimizeDialog = () => {
    setShowOptimizeDialog(false);
    setOptimizationResult(null);
    setSelectedForDeletion(new Set());
    setOptimizationProgress({
      phase: 'idle',
      categories: [],
      currentCategory: null,
      currentCategoryIndex: 0,
      totalCategories: 0,
      processedCategories: [],
      categoryResults: {}
    });
  };

  // Link Policies handlers
  const handleOpenPolicyDialog = async () => {
    setShowPolicyDialog(true);
    setPolicyStatus(null);
    setPolicyTab(null);
    setPolicyLinkProgress({ current: 0, total: 0, results: [] });
    try {
      const response = await axios.get(`${API}/audits/${auditId}/policy-status`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setPolicyStatus(response.data);
    } catch (err) {
      toast.error('Failed to load policy status');
    }
  };

  const handleLinkPolicies = async () => {
    if (!policyStatus?.needs_policy?.length) return;
    setLinkingPolicies(true);
    const results = [];
    const total = policyStatus.needs_policy.length;
    setPolicyLinkProgress({ current: 0, total, results: [] });

    for (let i = 0; i < total; i++) {
      const control = policyStatus.needs_policy[i];
      try {
        const resp = await axios.post(`${API}/audits/${auditId}/link-control-policy`,
          { control_id: control.id },
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
        );
        results.push({ name: control.policy_name, status: resp.data.status });
      } catch {
        results.push({ name: control.policy_name, status: 'error' });
      }
      setPolicyLinkProgress({ current: i + 1, total, results: [...results] });
    }

    setLinkingPolicies(false);
    toast.success(`Policies processed: ${results.filter(r => r.status !== 'error').length}/${total}`);
    // Refresh status
    try {
      const response = await axios.get(`${API}/audits/${auditId}/policy-status`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setPolicyStatus(response.data);
    } catch {}
    // Refresh controls
    fetchAuditData();
  };

  const handleAddManualControl = async () => {
    // Validation
    if (!controlForm.framework || !controlForm.control_name || !controlForm.description || !controlForm.category) {
      toast.error('Please fill in all required fields');
      return;
    }

    setAddingControl(true);
    try {
      const evidenceArray = controlForm.evidence_required
        .split(',')
        .map(e => e.trim())
        .filter(e => e.length > 0);

      await axios.post(
        `${API}/audits/${auditId}/controls`,
        {
          ...controlForm,
          evidence_required: evidenceArray
        },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );
      
      toast.success('Control added successfully');
      setShowAddControlDialog(false);
      
      // Reset form
      setControlForm({
        framework: '',
        control_name: '',
        description: '',
        category: '',
        evidence_required: '',
        type: 'manual'
      });
      
      // Refresh controls
      await fetchAuditData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add control');
      console.error(error);
    } finally {
      setAddingControl(false);
    }
  };

  const updateControlStatus = async (controlId, newStatus, naReason = null) => {
    try {
      let url = `${API}/controls/${controlId}/status?status=${newStatus}`;
      if (newStatus === 'not_applicable' && naReason) {
        url += `&na_reason=${encodeURIComponent(naReason)}`;
      }
      await axios.put(url);

      // Update local state
      setControls(controls.map(control =>
        control.id === controlId ? {
          ...control,
          status: newStatus,
          ...(newStatus === 'not_applicable' && naReason ? { na_reason: naReason } : {}),
        } : control
      ));
      
      // Refresh audit data to get updated progress
      const auditResponse = await axios.get(`${API}/audits/${auditId}`);
      setAudit(auditResponse.data);
      
      toast.success('Control status updated successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update control status');
    }
  };

  // Intercept attempts to set status="not_applicable" and prompt for a
  // mandatory reason. The reason is required by the backend; this dialog
  // mirrors that contract so users can't dispatch the request without one.
  const [naDialog, setNaDialog] = useState({ open: false, controlId: null, controlName: '' });
  const requestStatusChange = (controlId, newStatus) => {
    if (newStatus === 'not_applicable') {
      const c = controls.find(x => x.id === controlId);
      setNaDialog({ open: true, controlId, controlName: c?.control_name || '' });
      return;
    }
    updateControlStatus(controlId, newStatus);
  };

  const getFrameworkColor = (framework) => {
    const colors = {
      'SOC 2': 'bg-blue-100 text-blue-700 border-blue-200',
      'GDPR': 'bg-red-100 text-red-700 border-red-200',
      'ISO 27001': 'bg-purple-100 text-purple-700 border-purple-200',
      'PCI DSS': 'bg-orange-100 text-orange-700 border-orange-200',
      'HIPAA': 'bg-teal-100 text-teal-700 border-teal-200'
    };
    return colors[framework] || 'bg-gray-100 text-gray-700 border-gray-200';
  };

  // Renders framework badge(s) for a control — shows applicable_frameworks if available
  const renderFrameworkBadges = (control, className = "text-xs") => {
    const frameworks = control.applicable_frameworks && control.applicable_frameworks.length > 0
      ? control.applicable_frameworks
      : [control.framework];
    
    return frameworks.map((fw, i) => (
      <Badge key={i} variant="outline" className={`${getFrameworkColor(fw)} ${className}`}>
        {fw}
      </Badge>
    ));
  };

  // Renders category badge(s) for a control — shows applicable_categories if multi-category
  const renderCategoryBadges = (control, className = "text-xs") => {
    const cats = control.applicable_categories && control.applicable_categories.length > 1
      ? control.applicable_categories
      : null;
    if (!cats) return null;
    return cats.map((cat, i) => (
      <Badge key={`cat-${i}`} variant="outline" className={`bg-slate-50 text-slate-600 border-slate-200 ${className}`}>
        {cat}
      </Badge>
    ));
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'bg-yellow-100 text-yellow-700',
      'in_review': 'bg-blue-100 text-blue-700',
      'completed': 'bg-green-100 text-green-700',
      'not_applicable': 'bg-slate-100 text-slate-500'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const getTypeColor = (type) => {
    return type === 'automated' 
      ? 'bg-emerald-100 text-navy-700 border-emerald-200'
      : 'bg-slate-100 text-slate-700 border-slate-200';
  };

  const getTypeLabel = (type) => {
    if (type === 'automated') return 'Auto';
    return formatStatus(type);
  };

  const getStatsData = () => {
    const src = filteredControls;
    const totalControls = src.length;
    const completedControls = src.filter(c => c.status === 'completed').length;
    const pendingControls = src.filter(c => c.status === 'pending').length;
    const inReviewControls = src.filter(c => c.status === 'in_review').length;
    const notApplicableControls = src.filter(c => c.status === 'not_applicable').length;
    const automatedControls = src.filter(c => c.type === 'automated').length;

    return {
      total: totalControls,
      completed: completedControls,
      pending: pendingControls,
      inReview: inReviewControls,
      notApplicable: notApplicableControls,
      automated: automatedControls,
      manual: totalControls - automatedControls,
      progress: totalControls > 0 ? Math.round((completedControls / totalControls) * 100) : 0
    };
  };

  // ------------------------------------------------------------------
  // Export helpers (XLSX-first)
  //
  // We switched the primary export from CSV to XLSX in Feb 2026 because
  // Excel opens CSV with equal, narrow column widths — long text
  // columns like Description / Automation Guidance make the sheet look
  // "congested". XLSX lets us pre-set per-column widths so the sheet
  // opens legibly. CSV export is still available as a fallback.
  //
  // Each row's evidence_required (list of ~3-5 evidence names) is
  // ALSO exploded into individual columns "Evidence 1", "Evidence 2",
  // ... "Evidence N" so admins can scan each evidence per row without
  // splitting a semicolon-joined string.
  // ------------------------------------------------------------------
  const escapeCsvField = (field) => {
    if (field === null || field === undefined) return '';
    const str = String(field);
    if (str.includes(',') || str.includes('"') || str.includes('\n')) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  // Column-width preset for common headers. Anything not listed falls
  // back to a reasonable default. Widths are in Excel character units.
  const COLUMN_WIDTHS = {
    '#': 5,
    'Control Ref': 16,
    'Control Name': 40,
    'Description': 60,
    'Framework': 12,
    'Frameworks': 20,
    'Category': 22,
    'Categories': 26,
    'All Categories': 26,
    'Technology': 24,
    'Technologies': 28,
    'Tech Category': 20,
    'Type': 10,
    'Status': 14,
    'Automation Potential': 20,
    'Automation Guidance': 55,
    'Evidence Required': 60,
    'Evidence #': 10,
    'Evidence': 55,
    'Policy': 30,
  };
  const defaultColWidth = 22;
  const buildColWidths = (headers) =>
    headers.map((h) => ({
      wch: COLUMN_WIDTHS[h] ?? defaultColWidth,
    }));

  const buildMetaAoa = () => {
    const userJson = JSON.parse(localStorage.getItem('user') || 'null');
    const companyName = userJson?.company_name || '';
    return [
      ['Company', companyName],
      ['Audit', audit?.name || ''],
      ['Exported at', new Date().toISOString()],
      [],
    ];
  };

  const downloadXlsx = (rows, headers, filename, sheetName = 'Controls') => {
    const metaAoa = buildMetaAoa();
    const aoa = [...metaAoa, headers, ...rows];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = buildColWidths(headers);
    // Freeze the header row (accounting for the meta block above it)
    ws['!freeze'] = { xSplit: 0, ySplit: metaAoa.length + 1 };
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    const xlsxName = filename.endsWith('.xlsx') ? filename : filename.replace(/\.csv$/i, '') + '.xlsx';
    XLSX.writeFile(wb, xlsxName);
    toast.success(`Downloaded ${xlsxName}`);
  };

  const downloadCsv = (rows, headers, filename) => {
    // Kept as a fallback path for admins who prefer raw CSV. XLSX is
    // now the default because it opens legibly in Excel.
    const metaLines = buildMetaAoa()
      .map((row) => row.map(escapeCsvField).join(','));
    const csvContent = [
      ...metaLines,
      headers.map(escapeCsvField).join(','),
      ...rows.map((row) => row.map(escapeCsvField).join(',')),
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success(`Downloaded ${filename}`);
  };

  const getFrameworksString = (control) => {
    if (control.applicable_frameworks && control.applicable_frameworks.length > 0) {
      return control.applicable_frameworks.join('; ');
    }
    return control.framework || '';
  };

  const getCategoriesString = (control) => {
    if (control.applicable_categories && control.applicable_categories.length > 0) {
      return control.applicable_categories.join('; ');
    }
    return control.category || '';
  };

  const getTechNamesForControl = (control) => {
    if (!control.technology_ids || control.technology_ids.length === 0) return '';
    return control.technology_ids.map(id => {
      if (id === '__main_product__') return audit?.organization || 'Main Product';
      return technologyMap[id]?.name || id;
    }).join('; ');
  };

  // Build the list of evidence rows for one control. Each evidence item
  // produces its own row in the sheet — this reads much better than
  // stacking 3 evidences across columns because a control's evidence
  // strings can be long. When a control has no evidence we still emit a
  // single row so it isn't lost from the export.
  const _evidenceRowsFor = (control) => {
    const list = Array.isArray(control.evidence_required) ? control.evidence_required : [];
    if (list.length === 0) return [{ idx: '', text: '' }];
    return list.map((text, i) => ({ idx: i + 1, text: text || '' }));
  };

  const downloadCategoriesView = () => {
    const headers = [
      'Category', 'All Categories', 'Control Ref', 'Control Name',
      'Frameworks', 'Type', 'Status', 'Automation Potential',
      'Evidence #', 'Evidence',
      'Description',
    ];
    const sorted = [...filteredControls].sort((a, b) => (a.category || '').localeCompare(b.category || ''));
    const rows = [];
    sorted.forEach((c) => {
      _evidenceRowsFor(c).forEach((ev) => {
        rows.push([
          c.category,
          getCategoriesString(c),
          c.control_ref || '',
          c.control_name,
          getFrameworksString(c),
          c.type === 'automated' ? 'Automated' : 'Manual',
          formatStatus(c.status),
          c.automation_potential ? formatStatus(c.automation_potential) : '',
          ev.idx,
          ev.text,
          c.description || '',
        ]);
      });
    });
    const auditName = (audit?.name || 'audit').replace(/[^a-zA-Z0-9]/g, '_');
    downloadXlsx(rows, headers, `${auditName}_by_categories`, 'By Category');
  };

  const downloadTechnologiesView = () => {
    const headers = [
      'Technology', 'Tech Category', 'Control Ref', 'Control Name',
      'Frameworks', 'Categories', 'Type', 'Status', 'Automation Potential',
      'Evidence #', 'Evidence',
      'Automation Guidance', 'Description',
    ];
    const rows = [];
    filteredControls.forEach((c) => {
      const techIds = c.technology_ids && c.technology_ids.length > 0 ? c.technology_ids : ['_none_'];
      techIds.forEach((techId) => {
        let techName;
        let techCat;
        if (techId === '__main_product__') {
          techName = audit?.organization || 'Main Product';
          techCat = 'Main Product';
        } else if (techId === '_none_') {
          techName = 'Unassigned';
          techCat = '';
        } else {
          const info = technologyMap[techId];
          techName = info?.name || techId;
          techCat = info?.category || '';
        }
        _evidenceRowsFor(c).forEach((ev) => {
          rows.push([
            techName,
            techCat,
            c.control_ref || '',
            c.control_name,
            getFrameworksString(c),
            getCategoriesString(c),
            c.type === 'automated' ? 'Automated' : 'Manual',
            formatStatus(c.status),
            c.automation_potential ? formatStatus(c.automation_potential) : '',
            ev.idx,
            ev.text,
            c.automation_guidance || '',
            c.description || '',
          ]);
        });
      });
    });
    rows.sort((a, b) => a[0].localeCompare(b[0]));
    const auditName = (audit?.name || 'audit').replace(/[^a-zA-Z0-9]/g, '_');
    downloadXlsx(rows, headers, `${auditName}_by_technologies`, 'By Technology');
  };

  const downloadTableView = () => {
    const headers = [
      '#', 'Control Ref', 'Control Name',
      'Frameworks', 'Categories', 'Technologies',
      'Type', 'Status', 'Automation Potential',
      'Evidence #', 'Evidence',
      'Policy', 'Automation Guidance', 'Description',
    ];
    const rows = [];
    filteredControls.forEach((c, idx) => {
      _evidenceRowsFor(c).forEach((ev) => {
        rows.push([
          idx + 1,
          c.control_ref || '',
          c.control_name,
          getFrameworksString(c),
          getCategoriesString(c),
          getTechNamesForControl(c),
          c.type === 'automated' ? 'Automated' : 'Manual',
          formatStatus(c.status),
          c.automation_potential ? formatStatus(c.automation_potential) : '',
          ev.idx,
          ev.text,
          c.policy_name || '',
          c.automation_guidance || '',
          c.description || '',
        ]);
      });
    });
    const auditName = (audit?.name || 'audit').replace(/[^a-zA-Z0-9]/g, '_');
    downloadXlsx(rows, headers, `${auditName}_all_controls`, 'All Controls');
  };

  // Auto-Evidence flow (Feb 2026 collect-then-evaluate):
  //   1) POST /evaluate-evidence with {collect: true}
  //   2) Server returns 202 + job_id + plan + unsupported_techs
  //   3) We surface the plan immediately (so the user sees "Collecting
  //      fresh evidence from GitHub + Google Workspace..." and any
  //      unsupported-tech warnings) and then poll the job endpoint
  //      until status is `completed` or `failed`.
  //   4) On completion we toast the final counters and re-fetch.
  //
  // The legacy `collect=false` evaluate-only mode is still available for
  // power users via the dropdown — useful when you've just collected and
  // only want to re-run the matcher against the existing snapshot.
  const _formatUnsupportedTechsLine = (techs) => {
    if (!techs || techs.length === 0) return null;
    const parts = techs.map(t =>
      `${t.control_count} control${t.control_count === 1 ? '' : 's'} require ${t.name} evidence`,
    );
    return `${parts.join(' · ')} (collector not available yet)`;
  };

  const _pollAutoEvidenceJob = async (jobId) => {
    // ~2s × 90 = 3 min max poll budget. Each collect-then-evaluate run
    // typically takes 30-90s depending on org size; the budget covers
    // worst-case GH-large-org collection.
    const POLL_INTERVAL_MS = 2000;
    const MAX_ATTEMPTS = 90;
    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
      // eslint-disable-next-line no-await-in-loop
      const resp = await axios.get(
        `${API}/audits/${auditId}/auto-evidence-jobs/${jobId}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } },
      );
      const job = resp.data;
      if (job.status === 'completed') return { ok: true, job };
      if (job.status === 'failed') return { ok: false, job };
    }
    return { ok: false, job: null, timedOut: true };
  };

  // ── Auto-Evidence technology picker ──────────────────────────────
  const [autoEvDialogOpen, setAutoEvDialogOpen] = useState(false);
  const [autoEvSources, setAutoEvSources] = useState([]); // [{source,label,control_count,connected}]
  const [autoEvSelected, setAutoEvSelected] = useState([]); // selected source keys
  const [autoEvLoading, setAutoEvLoading] = useState(false);
  const [autoEvUnsupported, setAutoEvUnsupported] = useState([]);

  const openAutoEvidenceDialog = async () => {
    setAutoEvDialogOpen(true);
    setAutoEvLoading(true);
    try {
      const resp = await axios.get(
        `${API}/audits/${auditId}/evidence-sources`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } },
      );
      const srcs = resp.data?.sources || [];
      setAutoEvSources(srcs);
      setAutoEvUnsupported(resp.data?.unsupported_techs || []);
      setAutoEvSelected(srcs.filter((s) => s.connected).map((s) => s.source)); // default: all connected
    } catch (e) {
      toast.error('Failed to load technologies for this audit');
    } finally {
      setAutoEvLoading(false);
    }
  };

  const connectedAutoEvSources = autoEvSources.filter((s) => s.connected);
  const toggleAutoEvSource = (src) =>
    setAutoEvSelected((prev) => (prev.includes(src) ? prev.filter((x) => x !== src) : [...prev, src]));
  const allConnectedSelected =
    connectedAutoEvSources.length > 0 && connectedAutoEvSources.every((s) => autoEvSelected.includes(s.source));
  const toggleSelectAll = () =>
    setAutoEvSelected(allConnectedSelected ? [] : connectedAutoEvSources.map((s) => s.source));

  const runAutoEvidenceForSelected = async () => {
    const chosen = autoEvSelected.filter((s) => connectedAutoEvSources.some((x) => x.source === s));
    if (chosen.length === 0) {
      toast.error('Select at least one connected technology');
      return;
    }
    setAutoEvDialogOpen(false);
    await handleEvaluateEvidence({ collect: true, sources: chosen });
  };

  const handleEvaluateEvidence = async ({ collect = true, sources = null } = {}) => {
    if (evaluatingEvidence) return;
    setEvaluatingEvidence(true);
    try {
      const resp = await axios.post(
        `${API}/audits/${auditId}/evaluate-evidence`,
        { collect, ...(sources && sources.length ? { sources } : {}) },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } },
      );
      const data = resp.data || {};
      const unsupportedLine = _formatUnsupportedTechsLine(data.unsupported_techs);

      if (data.mode === 'collect_then_evaluate' && data.job_id) {
        // Phase 1: planning toast. Mentions what we're about to collect
        // and any unsupported techs the user needs to be aware of.
        const runnable = data.runnable_sources || [];
        const notConnected = data.not_connected_sources || [];
        const planLine = runnable.length
          ? `Collecting fresh evidence from ${runnable.join(' + ')}…`
          : 'No fresh collection needed — running matcher only…';
        toast.info(planLine, { duration: 4000 });
        if (notConnected.length) {
          toast.warning(
            `${notConnected.join(' + ')} controls present but no integration connected — skipping collection`,
            { duration: 7000 },
          );
        }
        if (unsupportedLine) {
          toast.warning(unsupportedLine, { duration: 8000 });
        }

        // Phase 2: poll until done.
        const result = await _pollAutoEvidenceJob(data.job_id);
        if (result.timedOut) {
          toast.error(
            'Auto-Evidence is still running in the background — check back in a few minutes.',
            { duration: 8000 },
          );
        } else if (!result.ok) {
          toast.error(
            `Auto-Evidence failed: ${result.job?.error || 'unknown error'}`,
            { duration: 8000 },
          );
        } else {
          const c = result.job?.counters || {};
          toast.success(
            `Auto-evidence: ${c.pass || 0} pass · ${c.fail || 0} fail · ${c.partial || 0} partial · ` +
            `${c.insufficient_evidence || 0} need-more · ${c.not_applicable || 0} N/A · ` +
            `${c.ai_broadcast_matched || 0} AI-matched (evaluated ${c.evaluated || 0}/${c.total || 0})`,
            { duration: 10000 },
          );
        }
      } else {
        // evaluate-only sync mode
        const c = data.counters || {};
        if (unsupportedLine) toast.warning(unsupportedLine, { duration: 8000 });
        toast.success(
          `Auto-evidence: ${c.pass || 0} pass · ${c.fail || 0} fail · ${c.partial || 0} partial · ` +
          `${c.insufficient_evidence || 0} need-more · ${c.not_applicable || 0} N/A · ` +
          `${c.ai_broadcast_matched || 0} AI-matched (evaluated ${c.evaluated || 0}/${c.total || 0})`,
          { duration: 10000 },
        );
      }
      await fetchAuditData();
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Evidence evaluation failed');
    } finally {
      setEvaluatingEvidence(false);
    }
  };


  const handleDeduplicateControls = async () => {
    if (deduplicating) return;
    setDeduplicating(true);
    setDedupElapsed(0);
    const timerInterval = setInterval(() => {
      setDedupElapsed(prev => prev + 1);
    }, 1000);
    try {
      const response = await axios.post(
        `${API}/audits/${auditId}/deduplicate-controls`,
        {},
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          timeout: 300000
        }
      );
      clearInterval(timerInterval);
      const { merged_count, pass1_merged, pass2_merged, elapsed_seconds } = response.data;
      setDedupElapsed(Math.round(elapsed_seconds || 0));
      if (merged_count > 0) {
        toast.success(`Deduplicated ${merged_count} controls (${pass1_merged || 0} within-framework, ${pass2_merged || 0} cross-framework) in ${elapsed_seconds}s`);
        const controlsResponse = await axios.get(`${API}/audits/${auditId}/controls`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        setControls(controlsResponse.data.filter(c => !c.is_deleted));
      } else {
        toast.info('No duplicate controls found');
      }
    } catch (error) {
      clearInterval(timerInterval);
      console.error('Dedup error:', error);
      toast.error('Failed to deduplicate controls');
    } finally {
      clearInterval(timerInterval);
      setDeduplicating(false);
    }
  };

  const handleDownloadCsv = () => {
    if (filteredControls.length === 0) {
      toast.error('No controls to download');
      return;
    }
    if (viewMode === 'category') downloadCategoriesView();
    else if (viewMode === 'technology') downloadTechnologiesView();
    else downloadTableView();
  };

  const stats = getStatsData();
  // ------------------------------------------------------------------
  // "Deleted" tab semantics.
  //
  // The Deleted tab surfaces EVERY soft-deleted control on the audit —
  // both controls archived as part of a framework/tech retire operation
  // AND controls you deleted individually via the per-row trash icon.
  // Filter pills (framework, tech, category) are derived from the same
  // deleted set so "All" shows everything and the retired items surface
  // as their own pills (red because they're in the deleted list).
  // ------------------------------------------------------------------
  const retiredFrameworkNames = useMemo(
    () => (audit?.retired_frameworks || []).map((r) => r.framework).filter(Boolean),
    [audit?.retired_frameworks],
  );
  const retiredTechIds = useMemo(
    () => (audit?.retired_technologies || []).map((r) => r.technology_id).filter(Boolean),
    [audit?.retired_technologies],
  );

  const frameworks = useMemo(() => {
    const src = showingDeleted ? deletedControls : controls;
    const set = new Set();
    src.forEach((c) => {
      if (c.framework) set.add(c.framework);
      if (showingDeleted) (c.applicable_frameworks || []).forEach((f) => f && set.add(f));
    });
    return [...set].filter(Boolean);
  }, [controls, deletedControls, showingDeleted]);
  // Categories filter list — in Delete view, categories on any soft-deleted control.
  const categories = useMemo(() => {
    const src = showingDeleted ? deletedControls : controls;
    return [...new Set(src.map((c) => c.category))].filter(Boolean);
  }, [controls, deletedControls, showingDeleted]);

  // Technology filter — in Delete view, all technologies that appear on
  // any soft-deleted control (retire-flow OR individual). This lets
  // GitHub (retired) and any tech tagged on an individually-deleted
  // control both surface as pills.
  const { techOptionsMap, techOptions, hasUntagged } = useMemo(() => {
    const map = new Map();
    let untagged = false;
    const sources = showingDeleted ? deletedControls : controls;
    sources.forEach((c) => {
      const ids = c.technology_ids || [];
      if (ids.length === 0) untagged = true;
      ids.forEach((id) => {
        if (!map.has(id)) {
          const info = technologyMap[id];
          const name = id === '__main_product__'
            ? (audit?.organization || 'Main Product')
            : (info?.name || id);
          map.set(id, { id, name, category: info?.category || '' });
        }
      });
    });
    return {
      techOptionsMap: map,
      techOptions: Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name)),
      hasUntagged: untagged,
    };
  }, [controls, deletedControls, showingDeleted, technologyMap, audit?.organization]);

  // Deleted-view: per-technology counts of the deleted controls that would
  // remain after applying every other active filter (framework, type, status,
  // overlap, auto-evidence, owner, and server-side search) — the technology
  // filter itself is intentionally NOT applied here so switching techs still
  // shows the counts of the other buckets under the current filter context.
  // A control with multiple technologies is counted under every applicable
  // technology; untagged controls contribute to the "No technology" bucket.
  // Auto-refreshes whenever the underlying state changes (delete / restore /
  // permanent-remove all call fetchAuditData → updates deletedControls).
  const deletedTechCounts = useMemo(() => {
    if (!showingDeleted) return { total: 0, byTech: {}, none: 0 };
    const base = searchResults ? (searchResults.deleted || []) : deletedControls;

    const passesNonTechFilters = (c) => {
      // Framework
      if (selectedFramework !== 'all') {
        const inList = (c.applicable_frameworks && c.applicable_frameworks.length > 0)
          ? c.applicable_frameworks.includes(selectedFramework)
          : c.framework === selectedFramework;
        if (!inList) return false;
      }
      // Type
      if (selectedType !== 'all' && c.type !== selectedType) return false;
      // Status
      if (selectedStatus !== 'all' && c.status !== selectedStatus) return false;
      // Overlap
      if (overlapFilter !== 'all') {
        const fwCount = (c.applicable_frameworks && c.applicable_frameworks.length) || 1;
        const catCount = (c.applicable_categories && c.applicable_categories.length) || 1;
        if (overlapFilter === 'multi_framework' && !(fwCount > 1)) return false;
        if (overlapFilter === 'multi_category' && !(catCount > 1)) return false;
        if (overlapFilter === 'overlapping' && !(fwCount > 1 || catCount > 1)) return false;
      }
      // Auto-evidence
      if (autoEvidenceFilter !== 'all') {
        const ae = c.auto_evidence;
        const isAutomated = c.type === 'automated';
        const hasVerdict = isAutomated && ae && !!ae.status;
        if (autoEvidenceFilter === 'has_any' && !hasVerdict) return false;
        else if (autoEvidenceFilter === 'manual' && isAutomated) return false;
        else if (autoEvidenceFilter === 'not_evaluated' && !(isAutomated && !hasVerdict)) return false;
        else if (!['has_any', 'manual', 'not_evaluated'].includes(autoEvidenceFilter)
          && !(hasVerdict && ae.status === autoEvidenceFilter)) return false;
      }
      // Owner
      if (ownerFilter === 'mine' && user?.id && c.reviewer_id !== user.id) return false;
      if (ownerFilter === 'unassigned' && c.reviewer_id) return false;
      if (typeof ownerFilter === 'string' && ownerFilter.startsWith('user:')) {
        const uid = ownerFilter.slice(5);
        if (c.reviewer_id !== uid) return false;
      }
      return true;
    };

    const byTech = {};
    let none = 0;
    let total = 0;
    base.forEach((c) => {
      if (!passesNonTechFilters(c)) return;
      total += 1;
      const ids = c.technology_ids || [];
      if (ids.length === 0) { none += 1; return; }
      ids.forEach((id) => { byTech[id] = (byTech[id] || 0) + 1; });
    });
    return { total, byTech, none };
  }, [
    showingDeleted, searchResults, deletedControls,
    selectedFramework, selectedType, selectedStatus, overlapFilter,
    autoEvidenceFilter, ownerFilter, user?.id,
  ]);
  const selectedTechLabel = selectedTechnology === 'all'
    ? 'All Technologies'
    : selectedTechnology === '__none__'
      ? 'No technology'
      : techOptionsMap.get(selectedTechnology)?.name || 'Technology';

  if (loading) {
    return (
      <div className="min-h-screen bg-appbg">
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy-600"></div>
        </div>
      </div>
    );
  }

  if (!audit) {
    return (
      <div className="min-h-screen bg-appbg flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Audit Not Found</h2>
          <p className="text-slate-600 mb-4">The requested audit could not be found.</p>
          <Button onClick={() => navigate(getDashboardUrl())} variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-appbg">
      {/* Top bar */}
      <div className="bg-white/80 backdrop-blur-xl border-b border-[#D7E0F2]">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(getDashboardUrl())}
            className="flex items-center gap-2 text-[#475569] hover:text-[#0F172A]"
            data-testid="back-to-dashboard"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Dashboard</span>
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Audit header */}
        <div className="mb-6 animate-fadeIn">
          <Card className="bg-white">
            <CardContent className="p-6">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-1.5 mb-3">
                    {audit.frameworks.map((framework) => (
                      <Badge
                        key={framework}
                        className="bg-[#EFF6FF] text-[#0F62FE] border border-[#BFDBFE] font-medium text-[11px] px-2.5 py-0.5 rounded-full"
                      >
                        {framework}
                      </Badge>
                    ))}
                  </div>
                  <h1 className="text-2xl font-bold text-[#0F172A] tracking-tight truncate">{audit.name}</h1>
                  <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 mt-2.5 text-sm text-[#64748B]">
                    {audit.organization && (
                      <span className="flex items-center gap-1.5">
                        <Building2 className="w-4 h-4 text-[#94A3B8]" />
                        {audit.organization}
                      </span>
                    )}
                    {audit.audit_ref && (
                      <span className="flex items-center gap-1.5">
                        Audit ID: <span className="font-mono text-[#475569]">{audit.audit_ref}</span>
                      </span>
                    )}
                    <span className="flex items-center gap-1.5">
                      <Calendar className="w-4 h-4 text-[#94A3B8]" />
                      Created {formatDate(audit.created_at)}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {isEmulated && (
                    <Badge className="bg-purple-50 text-purple-700 border border-purple-200 text-[11px]">Emulated</Badge>
                  )}
                  <Badge className={`${getStatusColor(audit.status)} border font-medium px-3 py-1 rounded-full`}>
                    {formatStatus(audit.status)}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5 animate-slideIn">
          {/* Audit Readiness */}
          <Card className="bg-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-[#0F172A]">Audit Readiness</h3>
                <Settings className="w-4 h-4 text-[#94A3B8]" />
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-bold text-[#0F172A] leading-none">{stats.total}</span>
                <span className="text-sm text-[#64748B]">Controls</span>
              </div>
              <p className="text-xs text-[#64748B] mt-1.5">
                {stats.completed} completed · {Math.max(stats.total - stats.completed, 0)} remaining
              </p>
              <Progress value={stats.progress} className="h-2 mt-3 bg-[#0F62FE]/12 [&>div]:bg-[#0F62FE] rounded-full" />
              <div className="mt-3">
                <p className="text-sm font-semibold text-[#0F62FE]">
                  {stats.completed === 0 ? 'Just getting started' : `${stats.completed} of ${stats.total} controls completed`}
                </p>
                {stats.completed === 0 && (
                  <p className="text-xs text-[#64748B] mt-0.5">
                    {stats.total} controls to review
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2 mt-4 pt-4 border-t border-[#F1F5F9]">
                <span className="flex items-center gap-1.5 bg-[#F1F5F9] text-[#475569] text-xs font-medium px-2.5 py-1 rounded-full">
                  <Settings className="w-3.5 h-3.5 text-[#0F62FE]" />
                  {stats.automated} Automated
                </span>
                <span className="flex items-center gap-1.5 bg-[#F1F5F9] text-[#475569] text-xs font-medium px-2.5 py-1 rounded-full">
                  <Users className="w-3.5 h-3.5 text-[#64748B]" />
                  {stats.manual} Manual
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Auto Evidence */}
          <Card className="bg-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-[#0F172A]">Auto Evidence</h3>
                <Zap className="w-4 h-4 text-[#94A3B8]" />
              </div>
              <div className="space-y-2">
                {[
                  { key: 'pass', icon: <CheckCircle2 className="w-4 h-4 text-[#047857]" />, label: 'Auto-satisfied', count: autoEvidenceCounts.satisfied },
                  { key: 'insufficient_evidence', icon: <AlertTriangle className="w-4 h-4 text-[#B45309]" />, label: 'Need more evidence', count: autoEvidenceCounts.needMore },
                  { key: 'fail', icon: <XCircle className="w-4 h-4 text-[#B91C1C]" />, label: 'Policy violations', count: autoEvidenceCounts.violations },
                  { key: 'not_applicable', icon: <MinusCircle className="w-4 h-4 text-[#94A3B8]" />, label: 'Not applicable', count: autoEvidenceCounts.na },
                ].map(row => (
                  <button
                    key={row.key}
                    onClick={() => setAutoEvidenceFilter(autoEvidenceFilter === row.key ? 'all' : row.key)}
                    data-testid={`auto-evidence-summary-${row.key}`}
                    className={`w-full flex items-center justify-between rounded-lg px-3 py-2 text-left transition-colors ${
                      autoEvidenceFilter === row.key ? 'bg-[#EFF6FF]' : 'hover:bg-[#F8FAFC]'
                    }`}
                  >
                    <span className="flex items-center gap-2.5 text-sm text-[#334155]">
                      {row.icon}
                      {row.label}
                    </span>
                    <span className="text-xs font-semibold text-[#475569] bg-[#F1F5F9] px-2 py-0.5 rounded-full min-w-[28px] text-center">
                      {row.count}
                    </span>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Controls by Category */}
        {categoryBreakdown.length > 0 && (
          <div className="mb-5 animate-slideIn">
            <Card className="bg-white">
              <CardContent className="p-6">
                <h3 className="text-sm font-semibold text-[#0F172A] mb-4">Controls by Category</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
                  {categoryBreakdown.map(([cat, count]) => {
                    const maxCount = categoryBreakdown[0][1] || 1;
                    const pct = Math.round((count / maxCount) * 100);
                    return (
                      <div key={cat}>
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span className="text-[#334155] font-medium truncate pr-2">{cat}</span>
                          <span className="text-[#64748B] shrink-0">{count}</span>
                        </div>
                        <div className="h-1.5 bg-[#EEF2F9] rounded-full overflow-hidden">
                          <div className="h-full bg-[#0F62FE] rounded-full" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Audit Settings (collapsible) — houses AI Prompt Versions */}
        {audit.prompt_versions_used && Object.keys(audit.prompt_versions_used).length > 0 && (
          <div className="mb-5 animate-slideIn">
            <Collapsible>
              <Card className="bg-white">
                <CollapsibleTrigger className="w-full" data-testid="audit-settings-trigger">
                  <div className="flex items-center justify-between px-5 py-3.5">
                    <span className="flex items-center gap-2 text-sm font-medium text-[#334155]">
                      <Settings2 className="w-4 h-4 text-[#64748B]" />
                      Audit Settings
                    </span>
                    <ChevronDown className="w-4 h-4 text-[#94A3B8]" />
                  </div>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="px-5 pb-5 border-t border-[#F1F5F9]">
                    <div className="flex items-center gap-2 flex-wrap pt-3.5">
                      <span className="flex items-center gap-1.5 text-xs font-medium text-[#64748B]">
                        <Sparkles className="w-3.5 h-3.5 text-[#0F62FE]" />
                        AI Prompt Versions Used:
                      </span>
                      {Object.entries(audit.prompt_versions_used).map(([framework, version]) => (
                        <Badge key={framework} variant="outline" className="bg-[#F8FAFC] font-mono text-[11px] border-[#E2E8F0]">
                          {framework}: {version}
                        </Badge>
                      ))}
                      {audit.last_regenerated_at && (
                        <span className="text-[11px] text-[#94A3B8] ml-1">
                          (Last regenerated: {formatDate(audit.last_regenerated_at)})
                        </span>
                      )}
                    </div>
                  </div>
                </CollapsibleContent>
              </Card>
            </Collapsible>
          </div>
        )}

        {/* Controls heading */}
        <div className="flex items-end justify-between gap-3 mb-3 mt-8 flex-wrap">
          <div>
            <h2 className="text-xl font-bold text-[#0F172A] tracking-tight">Audit Controls</h2>
          </div>
        </div>



        {/* Filters & Actions toolbar (top — drives summary cards + table) */}
        <div className="mb-6 animate-slideIn">
          <Card className="bg-white">
            <CardContent className="p-4">
              {/* Row 1: search + reset + actions */}
              <div className="flex items-center gap-3 flex-wrap">
                <div className="relative flex-1 min-w-[220px] max-w-[360px]">
                  <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <Input
                    placeholder="Search by name or ref…"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9 pr-9 w-full"
                    data-testid="search-controls"
                  />
                  {searching && (
                    <Loader2 className="w-4 h-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 animate-spin" />
                  )}
                </div>

                <div className="flex items-center gap-2 flex-wrap ml-auto">
                  {hasActiveFilters && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={resetAllFilters}
                      className="text-[#64748B] hover:text-[#0F172A]"
                      data-testid="reset-filters-btn"
                    >
                      <X className="w-4 h-4 mr-1" /> Reset Filters
                    </Button>
                  )}

                  {/* Actions dropdown */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm" className="border-[#D7E0F2] text-[#334155]" data-testid="actions-menu-btn">
                        <MoreHorizontal className="w-4 h-4 mr-2" /> Actions
                        <ChevronDown className="w-3.5 h-3.5 ml-1.5 opacity-60" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <DropdownMenuItem onClick={() => setShowAddControlDialog(true)} data-testid="add-control-btn">
                        <Plus className="w-4 h-4 mr-2" /> Add Control
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setShowBulkImportDialog(true)} data-testid="bulk-import-btn">
                        <Upload className="w-4 h-4 mr-2" /> Bulk Import
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={handleDownloadCsv} data-testid="download-csv-btn">
                        <Download className="w-4 h-4 mr-2" /> Download Excel
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setShowManageTechDialog(true)} data-testid="manage-technologies-btn">
                        <Server className="w-4 h-4 mr-2 text-slate-700" /> Manage Technologies
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setShowManageFwDialog(true)} data-testid="manage-frameworks-btn">
                        <ShieldCheck className="w-4 h-4 mr-2 text-slate-700" /> Manage Frameworks
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel className="text-xs text-[#94A3B8] font-normal">AI &amp; Evidence</DropdownMenuLabel>
                      <DropdownMenuItem onClick={handleOpenPolicyDialog} data-testid="link-policies-btn">
                        <Link2 className="w-4 h-4 mr-2 text-green-600" /> Link Policies
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setShowRegenerateDialog(true)}>
                        <RefreshCw className="w-4 h-4 mr-2 text-blue-600" /> Regenerate with AI
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setShowOptimizeDialog(true)} data-testid="optimize-with-ai-btn">
                        <Zap className="w-4 h-4 mr-2 text-purple-600" /> Optimize with AI
                      </DropdownMenuItem>
                      {audit?.frameworks?.length > 0 && (
                        <DropdownMenuItem onClick={handleDeduplicateControls} disabled={deduplicating} data-testid="deduplicate-btn">
                          {deduplicating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2 text-amber-600" />}
                          {deduplicating ? `Deduplicating... ${dedupElapsed}s` : 'Deduplicate'}
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem onClick={openAutoEvidenceDialog} disabled={evaluatingEvidence} data-testid="evaluate-evidence-btn">
                        {evaluatingEvidence ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2 text-indigo-600" />}
                        {evaluatingEvidence ? 'Evaluating...' : 'Auto-Evidence'}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>

              <Dialog open={autoEvDialogOpen} onOpenChange={setAutoEvDialogOpen}>
                <DialogContent className="max-w-md" data-testid="auto-evidence-dialog">
                  <DialogHeader>
                    <DialogTitle>Run Auto-Evidence</DialogTitle>
                    <DialogDescription>
                      Choose which connected technologies to collect & evaluate evidence for. Select all, or run them one at a time.
                    </DialogDescription>
                  </DialogHeader>

                  {autoEvLoading ? (
                    <div className="py-10 flex items-center justify-center text-slate-500" data-testid="auto-ev-loading">
                      <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading technologies…
                    </div>
                  ) : connectedAutoEvSources.length === 0 ? (
                    <div className="py-6 text-center text-sm text-slate-600" data-testid="auto-ev-empty">
                      No connected technologies for this audit. Connect an integration on the Technology page first.
                    </div>
                  ) : (
                    <div className="space-y-1.5 max-h-72 overflow-y-auto">
                      <button
                        type="button"
                        onClick={toggleSelectAll}
                        data-testid="auto-ev-select-all"
                        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors text-left"
                      >
                        <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${allConnectedSelected ? 'bg-indigo-600 border-indigo-600' : 'border-slate-300'}`}>
                          {allConnectedSelected && <CheckCircle2 className="w-3 h-3 text-white" />}
                        </span>
                        <span className="text-sm font-semibold text-slate-800">All technologies</span>
                        <span className="ml-auto text-xs text-slate-400">{connectedAutoEvSources.length}</span>
                      </button>

                      {autoEvSources.map((s) => {
                        const checked = autoEvSelected.includes(s.source);
                        return (
                          <button
                            key={s.source}
                            type="button"
                            disabled={!s.connected}
                            onClick={() => s.connected && toggleAutoEvSource(s.source)}
                            data-testid={`auto-ev-source-${s.source}`}
                            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border text-left transition-colors ${
                              s.connected ? 'border-slate-200 hover:bg-slate-50' : 'border-slate-100 bg-slate-50/60 cursor-not-allowed opacity-70'
                            }`}
                          >
                            <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${checked && s.connected ? 'bg-indigo-600 border-indigo-600' : 'border-slate-300'}`}>
                              {checked && s.connected && <CheckCircle2 className="w-3 h-3 text-white" />}
                            </span>
                            <Server className="w-4 h-4 text-slate-500 shrink-0" />
                            <span className="text-sm font-medium text-slate-800">{s.label}</span>
                            <span className="ml-auto flex items-center gap-2">
                              <span className="text-xs text-slate-400">{s.control_count} control{s.control_count === 1 ? '' : 's'}</span>
                              {!s.connected && <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-600 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">Not connected</span>}
                            </span>
                          </button>
                        );
                      })}

                      {autoEvUnsupported.length > 0 && (
                        <p className="text-[11px] text-slate-400 pt-1 px-1" data-testid="auto-ev-unsupported">
                          No automated collector for: {autoEvUnsupported.map((u) => (typeof u === 'string' ? u : u.name)).join(', ')}. Collect these manually.
                        </p>
                      )}
                    </div>
                  )}

                  <DialogFooter>
                    <Button variant="outline" onClick={() => setAutoEvDialogOpen(false)} data-testid="auto-ev-cancel">Cancel</Button>
                    <Button
                      onClick={runAutoEvidenceForSelected}
                      disabled={autoEvLoading || autoEvSelected.length === 0}
                      data-testid="auto-ev-run"
                      className="bg-indigo-600 hover:bg-indigo-700 text-white"
                    >
                      <Zap className="w-4 h-4 mr-2" /> Run ({autoEvSelected.filter((s) => connectedAutoEvSources.some((x) => x.source === s)).length})
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              {/* Row 2: filters fully expanded as pill rows (no dropdowns) */}
              <div className="space-y-2.5 mt-3 pt-3 border-t border-[#F1F5F9]">
                <div className="flex items-start gap-3">
                  <span className="text-[11px] font-semibold text-[#94A3B8] w-24 shrink-0 pt-1 uppercase tracking-wide">View</span>
                  <div className="flex items-center gap-2 flex-wrap">
                    {renderPills(
                      [
                        { value: 'table', label: 'All' },
                        { value: 'technology', label: 'Technologies' },
                        { value: 'category', label: 'Categories' },
                      ],
                      viewMode, setViewMode, 'view-mode-pills'
                    )}
                    <button
                      type="button"
                      onClick={() => setShowingDeleted((v) => !v)}
                      data-testid="deleted-view-toggle"
                      className={
                        `px-3 py-1.5 rounded-full text-[11px] font-semibold transition-colors border ` +
                        (showingDeleted
                          ? 'bg-rose-600 text-white border-rose-600 hover:bg-rose-700'
                          : 'bg-white text-rose-700 border-rose-200 hover:bg-rose-50')
                      }
                      title="Show soft-deleted and retired controls. Filters still apply."
                    >
                      Deleted
                    </button>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <span className="text-[11px] font-semibold text-[#94A3B8] w-24 shrink-0 pt-1 uppercase tracking-wide">Framework</span>
                  {renderPills(
                    [{ value: 'all', label: 'All' }, ...frameworks.map((f) => ({ value: f, label: f }))],
                    selectedFramework, setSelectedFramework, 'framework-filter', showingDeleted ? 'rose' : 'default'
                  )}
                </div>

                <div className="flex items-start gap-3">
                  <span className="text-[11px] font-semibold text-[#94A3B8] w-24 shrink-0 pt-1 uppercase tracking-wide">Type</span>
                  {renderPills(
                    [
                      { value: 'all', label: 'All' },
                      { value: 'automated', label: 'Auto' },
                      { value: 'manual', label: 'Manual' },
                    ],
                    selectedType, setSelectedType, 'type-filter'
                  )}
                </div>

                <div className="flex items-start gap-3">
                  <span className="text-[11px] font-semibold text-[#94A3B8] w-24 shrink-0 pt-1 uppercase tracking-wide">Status</span>
                  {renderPills(
                    [
                      { value: 'all', label: 'All' },
                      { value: 'pending', label: 'Pending' },
                      { value: 'in_review', label: 'In Review' },
                      { value: 'completed', label: 'Completed' },
                      { value: 'not_applicable', label: 'Not Applicable' },
                    ],
                    selectedStatus, setSelectedStatus, 'status-filter'
                  )}
                </div>

                <div className="flex items-start gap-3">
                  <span className="text-[11px] font-semibold text-[#94A3B8] w-24 shrink-0 pt-1 uppercase tracking-wide">Owner</span>
                  {renderPills(
                    [
                      { value: 'all', label: 'All' },
                      { value: 'mine', label: 'My tasks' },
                      ...assignedOwners.map((o) => ({
                        value: `user:${o.id}`,
                        label: o.name,
                      })),
                      { value: 'unassigned', label: 'Unassigned' },
                    ],
                    ownerFilter, setOwnerFilter, 'owner-filter'
                  )}
                </div>

                <div className="flex items-start gap-3">
                  <span className="text-[11px] font-semibold text-[#94A3B8] w-24 shrink-0 pt-1 uppercase tracking-wide">Category</span>
                  {renderPills(
                    [
                      { value: 'all', label: 'All' },
                      { value: 'overlapping', label: 'Overlapping' },
                      { value: 'multi_framework', label: 'Multi-Framework' },
                      { value: 'multi_category', label: 'Multi-Category' },
                    ],
                    overlapFilter, setOverlapFilter, 'overlap-filter'
                  )}
                </div>

                <div className="flex items-start gap-3">
                  <span className="text-[11px] font-semibold text-[#94A3B8] w-24 shrink-0 pt-1 uppercase tracking-wide">Auto-Evidence</span>
                  {renderPills(
                    [
                      { value: 'all', label: 'All' },
                      { value: 'has_any', label: 'With Evidence' },
                      { value: 'not_evaluated', label: 'Not evaluated' },
                      { value: 'manual', label: 'Manual' },
                      { value: 'pass', label: 'Passed ✓' },
                      { value: 'fail', label: 'Failed ✕' },
                      { value: 'insufficient_evidence', label: 'Insufficient ⚠' },
                      { value: 'partial', label: 'Partial ◐' },
                      { value: 'not_applicable', label: 'N/A' },
                    ],
                    autoEvidenceFilter, setAutoEvidenceFilter, 'auto-evidence-filter'
                  )}
                </div>

                {(techOptions.length > 0 || hasUntagged) && (
                  <div className="flex items-start gap-3">
                    <span className="text-[11px] font-semibold text-[#94A3B8] w-24 shrink-0 pt-1 uppercase tracking-wide">Technology</span>
                    {(() => {
                      // In the Deleted view we decorate each pill with a live
                      // count of soft-deleted controls per technology (respecting
                      // every other active filter) and drop any technology that
                      // would render as zero. The active-controls view keeps its
                      // previous shape — no counts, all techs shown — so the
                      // existing filter behavior is unchanged.
                      const withCount = (label, n) => (n != null ? `${label} (${n})` : label);
                      const opts = [];
                      if (showingDeleted) {
                        opts.push({ value: 'all', label: withCount('All deleted controls', deletedTechCounts.total) });
                        if (hasUntagged && deletedTechCounts.none > 0) {
                          opts.push({ value: '__none__', label: withCount('No technology', deletedTechCounts.none) });
                        }
                        techOptions.forEach((t) => {
                          const n = deletedTechCounts.byTech[t.id] || 0;
                          if (n > 0) opts.push({ value: t.id, label: withCount(t.name, n) });
                        });
                      } else {
                        opts.push({ value: 'all', label: 'All' });
                        if (hasUntagged) opts.push({ value: '__none__', label: 'No technology' });
                        techOptions.forEach((t) => opts.push({ value: t.id, label: t.name }));
                      }
                      return renderPills(
                        opts,
                        selectedTechnology, setSelectedTechnology, 'technology-filter', showingDeleted ? 'rose' : 'default'
                      );
                    })()}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filtered count — shown just below the filter card, above the table */}
        <p className="text-sm text-[#64748B] mb-3">
          <span className="font-semibold text-[#334155]">{filteredControls.length}</span> of{' '}
          {showingDeleted ? deletedControls.length : controls.length} shown
          {showingDeleted && (
            <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-rose-50 text-rose-700 border border-rose-200">
              Deleted / Retired view
            </span>
          )}
        </p>

        {/* Controls Section */}
        <Card className="border-0 shadow-soft bg-white/80 backdrop-blur-sm animate-slideIn">
          <CardContent className="p-0">
            {/* Category View */}
            {viewMode === 'category' && filteredControls.length > 0 && (
              <div className="p-4">
                {/* Category Summary Header */}
                <div className="flex items-center justify-between mb-4 pb-4 border-b border-slate-200">
                  <div className="flex items-center space-x-4">
                    <span className="text-sm font-medium text-slate-700">
                      {Object.keys(controlsByCategory).length} Categories
                    </span>
                    <span className="text-sm text-slate-500">|</span>
                    <span className="text-sm text-slate-600">
                      {filteredControls.length} Controls Total
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={expandAllCategories}
                      className="text-xs"
                    >
                      Expand All
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={collapseAllCategories}
                      className="text-xs"
                    >
                      Collapse All
                    </Button>
                  </div>
                </div>

                {/* Category List */}
                <div className="space-y-3">
                  {Object.entries(controlsByCategory).map(([category, categoryControls]) => {
                    const completedCount = categoryControls.filter(c => c.status === 'completed').length;
                    const pendingCount = categoryControls.filter(c => c.status === 'pending').length;
                    const inReviewCount = categoryControls.filter(c => c.status === 'in_review').length;
                    const isOpen = openCategories[category];
                    
                    return (
                      <Collapsible
                        key={category}
                        open={isOpen}
                        onOpenChange={() => toggleCategory(category)}
                      >
                        <CollapsibleTrigger className="w-full">
                          <div className="flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100 rounded-lg border border-slate-200 transition-colors cursor-pointer">
                            <div className="flex items-center space-x-3">
                              {isOpen ? (
                                <ChevronDown className="w-5 h-5 text-slate-500" />
                              ) : (
                                <ChevronRight className="w-5 h-5 text-slate-500" />
                              )}
                              <FolderOpen className="w-5 h-5 text-blue-600" />
                              <span className="font-semibold text-slate-800">{category}</span>
                              <Badge variant="outline" className="ml-2 bg-white">
                                {categoryControls.length} {categoryControls.length === 1 ? 'Control' : 'Controls'}
                              </Badge>
                            </div>
                            <div className="flex items-center space-x-3">
                              {completedCount > 0 && (
                                <Badge className="bg-green-100 text-green-700 border-green-200">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  {completedCount} Completed
                                </Badge>
                              )}
                              {inReviewCount > 0 && (
                                <Badge className="bg-blue-100 text-blue-700 border-blue-200">
                                  <Clock className="w-3 h-3 mr-1" />
                                  {inReviewCount} In Review
                                </Badge>
                              )}
                              {pendingCount > 0 && (
                                <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200">
                                  <AlertCircle className="w-3 h-3 mr-1" />
                                  {pendingCount} Pending
                                </Badge>
                              )}
                            </div>
                          </div>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <div className="ml-8 mt-2 space-y-2">
                            {categoryControls.map((control, controlIndex) => {
                              // Calculate global sequence number
                              const categoriesBeforeThis = Object.keys(controlsByCategory).slice(0, Object.keys(controlsByCategory).indexOf(category));
                              const controlsBeforeThisCategory = categoriesBeforeThis.reduce((sum, cat) => sum + controlsByCategory[cat].length, 0);
                              const sequenceNumber = controlsBeforeThisCategory + controlIndex + 1;
                              
                              return (
                              <div
                                key={control.id}
                                className="flex items-center justify-between p-3 bg-white border border-slate-200 rounded-lg hover:border-blue-300 transition-colors"
                              >
                                <div className="flex items-center space-x-3 flex-1 min-w-0 mr-4">
                                  <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0">
                                    <span className="text-xs font-semibold text-slate-600">#{sequenceNumber}</span>
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="min-w-0">
                                      <div className="flex items-center space-x-2 mb-1 min-w-0">
                                        {control.control_ref && (
                                          <span className="text-xs font-mono text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded flex-shrink-0">
                                            {control.control_ref}
                                          </span>
                                        )}
                                        {renderDeletedBadge(control)}
                                        <TruncatedTooltip
                                          content={<div className="font-semibold">{control.control_name}</div>}
                                        >
                                          <h4 className="font-medium text-slate-900 truncate cursor-default">
                                            {control.control_name}
                                          </h4>
                                        </TruncatedTooltip>
                                        {renderFrameworkBadges(control, "text-xs flex-shrink-0")}
                                        {control.applicable_categories && control.applicable_categories.length > 1 && (
                                          <Badge variant="outline" className="bg-slate-50 text-slate-500 border-slate-200 text-xs flex-shrink-0">
                                            +{control.applicable_categories.length - 1} categories
                                          </Badge>
                                        )}
                                        <Badge
                                          variant="outline"
                                          className={`${getTypeColor(control.type)} text-xs flex-shrink-0`}
                                        >
                                          {getTypeLabel(control.type)}
                                        </Badge>
                                        <AutoEvidenceBadge control={control} />
                                      </div>
                                      <p className="text-sm text-slate-600 line-clamp-1">
                                        {control.description}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                                <div className="flex items-center space-x-3 flex-shrink-0">
                                  <div className="flex flex-col items-end gap-1">
                                    <Select 
                                      value={control.status} 
                                      onValueChange={(value) => requestStatusChange(control.id, value)}
                                    >
                                      <SelectTrigger className={`w-28 h-8 text-xs ${getStatusColor(control.status)}`}>
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="pending">Pending</SelectItem>
                                        <SelectItem value="in_review">In Review</SelectItem>
                                        <SelectItem value="completed">Completed</SelectItem>
                                        <SelectItem value="not_applicable">Not Applicable</SelectItem>
                                      </SelectContent>
                                    </Select>
                                    <EvidenceProgress control={control} compact />
                                  </div>
                                  <ControlRowActions
                                    control={control}
                                    onView={() => setModalControlId(control.id)}
                                    onDelete={() => openDeleteConfirm(control)}
                                  />
                                </div>
                              </div>
                              );
                            })}
                          </div>
                        </CollapsibleContent>
                      </Collapsible>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Table View */}
            {viewMode === 'table' && filteredControls.length > 0 && (
              <div className="w-full overflow-x-hidden">
                <table className="w-full max-w-full table-fixed border-collapse">
                  <colgroup>
                    <col style={{ width: '4%' }} />
                    <col style={{ width: '30%' }} />
                    <col style={{ width: '10%' }} />
                    <col style={{ width: '12%' }} />
                    <col style={{ width: '7%' }} />
                    <col style={{ width: '12%' }} />
                    <col style={{ width: '17%' }} />
                    <col style={{ width: '8%' }} />
                  </colgroup>
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left px-3 py-3 font-semibold text-slate-700 align-top">#</th>
                      <th className="text-left px-3 py-3 font-semibold text-slate-700 align-top">Control</th>
                      <th className="text-left px-3 py-3 font-semibold text-slate-700 align-top">Framework</th>
                      <th className="text-left px-3 py-3 font-semibold text-slate-700 align-top">Category</th>
                      <th className="text-left px-3 py-3 font-semibold text-slate-700 align-top">Type</th>
                      <th className="text-left px-3 py-3 font-semibold text-slate-700 align-top">Status</th>
                      <th className="text-left px-3 py-3 font-semibold text-slate-700 align-top">Auto-Evidence</th>
                      <th className="text-right px-3 py-3 font-semibold text-slate-700 align-top">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredControls.map((control, index) => (
                      <tr 
                        key={control.id} 
                        className={`border-b border-slate-200 hover:bg-slate-50 transition-colors ${
                          index % 2 === 0 ? 'bg-white' : 'bg-slate-25'
                        }`}
                        data-testid={`control-row-${control.id}`}
                      >
                        <td className="px-3 py-3 align-top">
                          <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center mt-0.5">
                            <span className="text-xs font-semibold text-slate-600">#{index + 1}</span>
                          </div>
                        </td>
                        <td className="px-3 py-3 align-top">
                          <div>
                            {control.control_ref && (
                              <span className="text-xs font-mono text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded mb-1 inline-block">
                                {control.control_ref}
                              </span>
                            )}
                            {renderDeletedBadge(control)}
                            <div>
                              <TruncatedTooltip
                              >
                                <h4 className="font-medium text-slate-900 mb-1 line-clamp-2 cursor-default">
                                  {control.control_name}
                                </h4>
                              </TruncatedTooltip>
                              <p className="text-sm text-slate-600 line-clamp-2">
                                {control.description}
                              </p>
                            </div>
                            {control.is_deleted && control.deleted_by_name && control.deleted_by_name.includes('merged into') && (() => {
                              const mergedIntoId = control.deleted_by_name.split('merged into ')[1];
                              const retainedControl = controls.find(c => c.id === mergedIntoId) || deletedControls.find(c => c.id === mergedIntoId);
                              return (
                                <button
                                  onClick={(e) => { e.stopPropagation(); setModalControlId(mergedIntoId); }}
                                  className="mt-1.5 inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline"
                                  data-testid={`merged-into-link-${control.id}`}
                                >
                                  <Link2 className="w-3 h-3" />
                                  Merged into: {retainedControl ? retainedControl.control_name : mergedIntoId}
                                </button>
                              );
                            })()}
                          </div>
                        </td>
                        <td className="px-3 py-3 align-top">
                          <div className="flex flex-wrap gap-1">
                            {renderFrameworkBadges(control)}
                          </div>
                        </td>
                        <td className="px-3 py-3 align-top">
                          <div className="flex flex-wrap gap-1">
                            {control.applicable_categories && control.applicable_categories.length > 1 ? (
                              control.applicable_categories.map((cat, i) => (
                                <Badge key={i} variant="outline" className="bg-slate-50 text-slate-600 border-slate-200 text-xs">
                                  {cat}
                                </Badge>
                              ))
                            ) : (
                              <span className="text-sm text-slate-700 font-medium">
                                {control.category}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-3 align-top">
                          <Badge 
                            variant="outline"
                            className={`${getTypeColor(control.type)} text-xs`}
                          >
                            {getTypeLabel(control.type)}
                          </Badge>
                        </td>
                        <td className="px-3 py-3 align-top">
                          <div className="flex flex-col gap-1.5 w-28">
                            <Select 
                              value={control.status} 
                              onValueChange={(value) => requestStatusChange(control.id, value)}
                              disabled={control.is_deleted}
                            >
                              <SelectTrigger className={`w-28 h-8 text-xs ${getStatusColor(control.status)}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="pending">Pending</SelectItem>
                                <SelectItem value="in_review">In Review</SelectItem>
                                <SelectItem value="completed">Completed</SelectItem>
                                <SelectItem value="not_applicable">Not Applicable</SelectItem>
                              </SelectContent>
                            </Select>
                            <EvidenceProgress control={control} uniformWidth />
                            {control.reviewer_id && (
                              <div className="text-xs text-slate-500 leading-tight" data-testid={`control-owner-inline-${control.id}`}>
                                <span className="font-semibold">Owner:</span>{' '}
                                <span className="text-slate-700">{control.reviewer_name || (control.reviewer_email ? control.reviewer_email.split('@')[0] : 'Owner')}</span>
                                {control.owner_needs_reassignment && (
                                  <span className="ml-1.5 text-[10px] uppercase tracking-wide text-amber-700 bg-amber-50 border border-amber-200 rounded px-1 py-0.5" data-testid={`owner-needs-reassignment-${control.id}`}>Reassign</span>
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-3 align-top">
                          <AutoEvidenceBadge control={control} />
                        </td>
                        <td className="px-3 py-3 align-top text-right">
                          <TooltipProvider delayDuration={0}>
                            <div className="flex items-center justify-end gap-1">
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => setModalControlId(control.id)}
                                    className="h-8 w-8 text-slate-600 hover:text-navy-700 hover:bg-slate-100"
                                    data-testid={`view-control-${control.id}`}
                                  >
                                    <ExternalLink className="w-4 h-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent className="bg-slate-900 text-white text-xs">View details</TooltipContent>
                              </Tooltip>
                              {!control.is_deleted && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => openDeleteConfirm(control)}
                                      className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent className="bg-slate-900 text-white text-xs">Delete</TooltipContent>
                                </Tooltip>
                              )}
                            </div>
                          </TooltipProvider>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Technology View */}
            {viewMode === 'technology' && filteredControls.length > 0 && (
              <div className="p-4">
                {/* Technology Summary Header */}
                <div className="flex items-center justify-between mb-4 pb-4 border-b border-slate-200">
                  <div className="flex items-center space-x-4">
                    <span className="text-sm font-medium text-slate-700">
                      Controls organized by Technology
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        // Expand all - get all tech IDs from the current controls
                        const allTechIds = new Set();
                        filteredControls.forEach(control => {
                          if (control.technology_ids) {
                            control.technology_ids.forEach(id => allTechIds.add(id));
                          }
                        });
                        const expanded = { others: true };
                        allTechIds.forEach(id => { expanded[id] = true; });
                        setOpenTechnologies(expanded);
                      }}
                      className="text-xs text-purple-600 hover:text-purple-700"
                    >
                      <ChevronDown className="w-4 h-4 mr-1" />
                      Expand All
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setOpenTechnologies({})}
                      className="text-xs text-slate-600 hover:text-slate-700"
                    >
                      <ChevronRight className="w-4 h-4 mr-1" />
                      Collapse All
                    </Button>
                  </div>
                </div>
                
                {/* Group controls by technology */}
                {(() => {
                  // Special constant for Main Product
                  const MAIN_PRODUCT_ID = "__main_product__";
                  
                  // Build technology groups
                  const controlsByTech = {};
                  const controlsWithoutTech = [];
                  
                  filteredControls.forEach(control => {
                    if (control.technology_ids && control.technology_ids.length > 0) {
                      control.technology_ids.forEach(techId => {
                        if (!controlsByTech[techId]) {
                          controlsByTech[techId] = [];
                        }
                        controlsByTech[techId].push(control);
                      });
                    } else {
                      controlsWithoutTech.push(control);
                    }
                  });
                  
                  // Separate Main Product from other technologies
                  const mainProductControls = controlsByTech[MAIN_PRODUCT_ID] || [];
                  delete controlsByTech[MAIN_PRODUCT_ID];
                  
                  // Sort other technologies by name
                  const sortedTechIds = Object.keys(controlsByTech).sort((a, b) => {
                    const nameA = technologyMap[a]?.name || 'Unknown';
                    const nameB = technologyMap[b]?.name || 'Unknown';
                    return nameA.localeCompare(nameB);
                  });
                  
                  // Get organization name for Main Product display
                  const mainProductName = audit?.organization || 'Main Product';
                  
                  return (
                    <div className="space-y-4">
                      {/* Main Product Section - Always First */}
                      {mainProductControls.length > 0 && (
                        <Collapsible 
                          open={openTechnologies[MAIN_PRODUCT_ID] === true}
                          onOpenChange={(open) => setOpenTechnologies(prev => ({ ...prev, [MAIN_PRODUCT_ID]: open }))}
                        >
                          <CollapsibleTrigger asChild>
                            <div className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border-2 border-blue-200 cursor-pointer hover:from-blue-100 hover:to-indigo-100 transition-colors">
                              <div className="flex items-center space-x-3">
                                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center shadow-sm">
                                  <Shield className="w-5 h-5 text-white" />
                                </div>
                                <div>
                                  <h3 className="font-semibold text-slate-900">{mainProductName}</h3>
                                  <p className="text-xs text-blue-600 font-medium">Main Product / Application</p>
                                </div>
                                <Badge className="bg-blue-100 text-blue-700 border-blue-300">
                                  {mainProductControls.length} {mainProductControls.length === 1 ? 'Control' : 'Controls'}
                                </Badge>
                              </div>
                              <div className="flex items-center space-x-2">
                                {openTechnologies[MAIN_PRODUCT_ID] === true ? (
                                  <ChevronDown className="w-5 h-5 text-blue-500" />
                                ) : (
                                  <ChevronRight className="w-5 h-5 text-blue-500" />
                                )}
                              </div>
                            </div>
                          </CollapsibleTrigger>
                          <CollapsibleContent>
                            <div className="space-y-2 mt-2 ml-4 pl-4 border-l-2 border-blue-200">
                              {mainProductControls.map((control, idx) => (
                                <div 
                                  key={`main-${control.id}`}
                                  className="p-4 bg-white border border-slate-200 rounded-lg hover:shadow-sm transition-shadow"
                                  data-testid={`main-product-control-${control.id}`}
                                >
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2 mb-2">
                                        <span className="text-xs font-mono text-slate-400">#{idx + 1}</span>
                                        {control.control_ref && (
                                          <span className="text-xs font-mono text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded">
                                            {control.control_ref}
                                          </span>
                                        )}
                                        {renderDeletedBadge(control)}
                                        <TruncatedTooltip
                                          content={<div className="font-semibold">{control.control_name}</div>}
                                        >
                                          <h4 className="font-medium text-slate-900 cursor-default truncate">{control.control_name}</h4>
                                        </TruncatedTooltip>
                                        {control.automation_potential && (
                                          <Badge 
                                            className={`text-xs ${
                                              control.automation_potential === 'high' 
                                                ? 'bg-green-100 text-green-700 border-green-200' 
                                                : control.automation_potential === 'medium'
                                                ? 'bg-yellow-100 text-yellow-700 border-yellow-200'
                                                : 'bg-slate-100 text-slate-700 border-slate-200'
                                            }`}
                                          >
                                            <Zap className="w-3 h-3 mr-1" />
                                            {formatStatus(control.automation_potential)} Automation
                                          </Badge>
                                        )}
                                      </div>
                                      <p className="text-sm text-slate-600 mb-2">{control.description}</p>
                                      {control.automation_guidance && (
                                        <div className="bg-blue-50 border border-blue-200 rounded p-2 mt-2">
                                          <p className="text-xs text-blue-700">
                                            <Cpu className="w-3 h-3 inline mr-1" />
                                            <strong>Integration guidance:</strong> {control.automation_guidance}
                                          </p>
                                        </div>
                                      )}
                                      <div className="flex items-center gap-2 mt-3 flex-wrap">
                                        {renderFrameworkBadges(control)}
                                        <Badge variant="outline" className={`${getTypeColor(control.type)} text-xs`}>
                                          {getTypeLabel(control.type)}
                                        </Badge>
                                        <Badge variant="outline" className={`${getStatusColor(control.status)} text-xs`}>
                                          {formatStatus(control.status)}
                                        </Badge>
                                        <AutoEvidenceBadge control={control} />
                                      </div>
                                    </div>
                                    <ControlRowActions
                                      control={control}
                                      onView={() => setModalControlId(control.id)}
                                      onDelete={() => openDeleteConfirm(control)}
                                    />
                                  </div>
                                </div>
                              ))}
                            </div>
                          </CollapsibleContent>
                        </Collapsible>
                      )}
                      
                      {/* Divider between Main Product and Third-Party Technologies */}
                      {mainProductControls.length > 0 && sortedTechIds.length > 0 && (
                        <div className="flex items-center gap-3 py-2">
                          <div className="flex-1 border-t border-slate-200"></div>
                          <span className="text-xs text-slate-500 font-medium">Third-Party Technologies</span>
                          <div className="flex-1 border-t border-slate-200"></div>
                        </div>
                      )}
                      
                      {/* Third-Party Technologies */}
                      {sortedTechIds.map((techId) => {
                        const techInfo = technologyMap[techId] || { name: 'Unknown', category: '', emoji: '📦' };
                        const techControls = controlsByTech[techId];
                        const isOpen = openTechnologies[techId] === true; // Default collapsed
                        
                        return (
                          <Collapsible 
                            key={techId} 
                            open={isOpen}
                            onOpenChange={(open) => setOpenTechnologies(prev => ({ ...prev, [techId]: open }))}
                          >
                            <CollapsibleTrigger asChild>
                              <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg border border-purple-100 cursor-pointer hover:bg-purple-100 transition-colors">
                                <div className="flex items-center space-x-3">
                                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                                    <span className="text-lg">{techInfo.emoji || '📦'}</span>
                                  </div>
                                  <div>
                                    <h3 className="font-medium text-slate-900">{techInfo.name}</h3>
                                    <p className="text-xs text-slate-500">{techInfo.category}</p>
                                  </div>
                                  <Badge className="bg-purple-100 text-purple-700 border-purple-200">
                                    {techControls.length} {techControls.length === 1 ? 'Control' : 'Controls'}
                                  </Badge>
                                </div>
                                <div className="flex items-center space-x-2">
                                  {isOpen ? (
                                    <ChevronDown className="w-5 h-5 text-slate-400" />
                                  ) : (
                                    <ChevronRight className="w-5 h-5 text-slate-400" />
                                  )}
                                </div>
                              </div>
                            </CollapsibleTrigger>
                            <CollapsibleContent>
                              <div className="space-y-2 mt-2 ml-4 pl-4 border-l-2 border-purple-100">
                                {techControls.map((control, idx) => (
                                  <div 
                                    key={`${techId}-${control.id}`}
                                    className="p-4 bg-white border border-slate-200 rounded-lg hover:shadow-sm transition-shadow"
                                    data-testid={`tech-control-${control.id}`}
                                  >
                                    <div className="flex items-start justify-between">
                                      <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-2">
                                          <span className="text-xs font-mono text-slate-400">#{idx + 1}</span>
                                          {control.control_ref && (
                                            <span className="text-xs font-mono text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded">
                                              {control.control_ref}
                                            </span>
                                          )}
                                          <TruncatedTooltip
                                          content={<div className="font-semibold">{control.control_name}</div>}
                                        >
                                          <h4 className="font-medium text-slate-900 cursor-default truncate">{control.control_name}</h4>
                                        </TruncatedTooltip>
                                          {control.automation_potential && (
                                            <Badge 
                                              className={`text-xs ${
                                                control.automation_potential === 'high' 
                                                  ? 'bg-green-100 text-green-700 border-green-200' 
                                                  : control.automation_potential === 'medium'
                                                  ? 'bg-yellow-100 text-yellow-700 border-yellow-200'
                                                  : 'bg-slate-100 text-slate-700 border-slate-200'
                                              }`}
                                            >
                                              <Zap className="w-3 h-3 mr-1" />
                                              {formatStatus(control.automation_potential)} Automation
                                            </Badge>
                                          )}
                                        </div>
                                        <p className="text-sm text-slate-600 mb-2">{control.description}</p>
                                        {control.automation_guidance && (
                                          <div className="bg-blue-50 border border-blue-200 rounded p-2 mt-2">
                                            <p className="text-xs text-blue-700">
                                              <Cpu className="w-3 h-3 inline mr-1" />
                                              <strong>Integration guidance:</strong> {control.automation_guidance}
                                            </p>
                                          </div>
                                        )}
                                        <div className="flex items-center gap-2 mt-3 flex-wrap">
                                          {renderFrameworkBadges(control)}
                                          <Badge variant="outline" className={`${getTypeColor(control.type)} text-xs`}>
                                            {getTypeLabel(control.type)}
                                          </Badge>
                                          <Badge variant="outline" className={`${getStatusColor(control.status)} text-xs`}>
                                            {formatStatus(control.status)}
                                          </Badge>
                                          <AutoEvidenceBadge control={control} />
                                        </div>
                                      </div>
                                      <ControlRowActions
                                        control={control}
                                        onView={() => setModalControlId(control.id)}
                                        onDelete={() => openDeleteConfirm(control)}
                                      />
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </CollapsibleContent>
                          </Collapsible>
                        );
                      })}
                      
                      {/* Controls without technology association */}
                      {controlsWithoutTech.length > 0 && (
                        <Collapsible 
                          open={openTechnologies['others'] === true}
                          onOpenChange={(open) => setOpenTechnologies(prev => ({ ...prev, others: open }))}
                        >
                          <CollapsibleTrigger asChild>
                            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200 cursor-pointer hover:bg-slate-100 transition-colors">
                              <div className="flex items-center space-x-3">
                                <div className="w-10 h-10 bg-slate-200 rounded-lg flex items-center justify-center">
                                  <FolderOpen className="w-5 h-5 text-slate-500" />
                                </div>
                                <div>
                                  <h3 className="font-medium text-slate-900">Others</h3>
                                  <p className="text-xs text-slate-500">Controls without technology association</p>
                                </div>
                                <Badge className="bg-slate-200 text-slate-700">
                                  {controlsWithoutTech.length} control{controlsWithoutTech.length !== 1 ? 's' : ''}
                                </Badge>
                              </div>
                              <div className="flex items-center space-x-2">
                                {openTechnologies['others'] === true ? (
                                  <ChevronDown className="w-5 h-5 text-slate-400" />
                                ) : (
                                  <ChevronRight className="w-5 h-5 text-slate-400" />
                                )}
                              </div>
                            </div>
                          </CollapsibleTrigger>
                          <CollapsibleContent>
                            <div className="space-y-2 mt-2 ml-4 pl-4 border-l-2 border-slate-200">
                              {controlsWithoutTech.map((control, idx) => (
                                <div 
                                  key={control.id}
                                  className="p-4 bg-white border border-slate-200 rounded-lg hover:shadow-sm transition-shadow"
                                >
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2 mb-2">
                                        <span className="text-xs font-mono text-slate-400">#{idx + 1}</span>
                                        {control.control_ref && (
                                          <span className="text-xs font-mono text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded">
                                            {control.control_ref}
                                          </span>
                                        )}
                                        {renderDeletedBadge(control)}
                                        <TruncatedTooltip
                                          content={<div className="font-semibold">{control.control_name}</div>}
                                        >
                                          <h4 className="font-medium text-slate-900 cursor-default truncate">{control.control_name}</h4>
                                        </TruncatedTooltip>
                                      </div>
                                      <p className="text-sm text-slate-600 mb-2">{control.description}</p>
                                      <div className="flex items-center gap-2 mt-3 flex-wrap">
                                        {renderFrameworkBadges(control)}
                                        <Badge variant="outline" className={`${getTypeColor(control.type)} text-xs`}>
                                          {getTypeLabel(control.type)}
                                        </Badge>
                                        <Badge variant="outline" className={`${getStatusColor(control.status)} text-xs`}>
                                          {formatStatus(control.status)}
                                        </Badge>
                                        <AutoEvidenceBadge control={control} />
                                      </div>
                                    </div>
                                    <ControlRowActions
                                      control={control}
                                      onView={() => setModalControlId(control.id)}
                                      onDelete={() => openDeleteConfirm(control)}
                                    />
                                  </div>
                                </div>
                              ))}
                            </div>
                          </CollapsibleContent>
                        </Collapsible>
                      )}
                      
                      {/* No technology associations at all */}
                      {sortedTechIds.length === 0 && controlsWithoutTech.length === 0 && (
                        <div className="text-center py-8 bg-slate-50 rounded-lg border border-slate-200">
                          <Server className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                          <p className="text-slate-600 font-medium">No technology associations</p>
                          <p className="text-sm text-slate-500 mt-1">
                            Controls will be associated with technologies when you create a new audit with technology selections
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}

            {filteredControls.length === 0 && (
              <div className="text-center py-12">
                <AlertCircle className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                <h4 className="text-lg font-medium text-slate-900 mb-2">No controls found</h4>
                <p className="text-slate-600 mb-4">
                  {controls.length === 0 
                    ? 'No controls have been generated for this audit yet.'
                    : 'Try adjusting your filters to see more results.'
                  }
                </p>
                {controls.length === 0 && (
                  <Button
                    onClick={() => setShowAddControlDialog(true)}
                    className="bg-navy-600 hover:bg-navy-700"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Your First Control
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Regenerate Controls Dialog */}
      <Dialog open={showRegenerateDialog} onOpenChange={closeRegenerateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-blue-600" />
              Regenerate Controls with AI
            </DialogTitle>
            <DialogDescription>
              AI will identify and generate controls for each category based on your audit frameworks.
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto py-4">
            {/* Initial State */}
            {!regenerating && regenerationProgress.phase === 'idle' && (
              <div className="text-center py-6">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <RefreshCw className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                  Ready to Regenerate Controls
                </h3>
                <p className="text-slate-600 max-w-md mx-auto mb-4">
                  AI will analyze your {audit?.frameworks?.join(', ')} audit and generate comprehensive controls for each category.
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-md mx-auto mb-6 text-left">
                  <h4 className="font-medium text-blue-800 mb-2 flex items-center gap-2">
                    <Lightbulb className="w-4 h-4" />
                    Smart Merge Strategy
                  </h4>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li>• AI identifies all required categories</li>
                    <li>• Existing controls are updated, not duplicated</li>
                    <li>• New controls are added to fill gaps</li>
                    <li>• Status and evidence are preserved</li>
                  </ul>
                </div>
                {/* Prompt Template Versions */}
                {audit?.prompt_versions_used && Object.keys(audit.prompt_versions_used).length > 0 && (
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 max-w-md mx-auto mb-6" data-testid="regenerate-prompt-versions">
                    <div className="flex items-center gap-2 text-xs text-slate-500 mb-2">
                      <FileText className="w-3.5 h-3.5" />
                      <span className="font-medium">Current Prompt Templates</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5 justify-center">
                      {Object.entries(audit.prompt_versions_used).map(([framework, version]) => (
                        <Badge key={framework} variant="outline" className="bg-white font-mono text-xs" data-testid={`prompt-version-${framework}`}>
                          {framework}: {version}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                <Button
                  onClick={handleRegenerateControls}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="start-regeneration-btn"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Start Regeneration
                </Button>
              </div>
            )}
            
            {/* Planning Phase */}
            {regenerating && regenerationProgress.phase === 'planning' && (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                  Planning Regeneration...
                </h3>
                <p className="text-slate-600">
                  AI is identifying categories needed for your audit
                </p>
              </div>
            )}
            
            {/* Generating Phase - With Progress */}
            {regenerating && regenerationProgress.phase === 'generating' && (
              <div className="py-4">
                <div className="text-center mb-6">
                  <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Loader2 className="w-7 h-7 text-blue-600 animate-spin" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900">
                    Generating Controls by Category
                  </h3>
                </div>
                
                {/* Progress Bar */}
                <div className="max-w-lg mx-auto mb-4">
                  <div className="flex items-center justify-between text-sm text-slate-600 mb-2">
                    <span>Progress</span>
                    <span>{regenerationProgress.processedCategories.length} of {regenerationProgress.totalCategories} categories</span>
                  </div>
                  <Progress 
                    value={(regenerationProgress.processedCategories.length / regenerationProgress.totalCategories) * 100} 
                    className="h-3"
                  />
                </div>
                
                {/* Current Category */}
                {regenerationProgress.currentCategory && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-lg mx-auto mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <FolderOpen className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-blue-600 font-medium">Currently Generating</p>
                        <p className="text-slate-900 font-semibold">{regenerationProgress.currentCategory}</p>
                      </div>
                      <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                    </div>
                  </div>
                )}
                
                {/* Running Totals */}
                <div className="flex justify-center gap-6 mb-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-green-600">{regenerationProgress.totalAdded}</p>
                    <p className="text-xs text-slate-600">Controls Added</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-blue-600">{regenerationProgress.totalUpdated}</p>
                    <p className="text-xs text-slate-600">Controls Updated</p>
                  </div>
                </div>
                
                {/* Categories Progress List */}
                <div className="max-w-lg mx-auto">
                  <h4 className="text-sm font-medium text-slate-700 mb-2">Category Progress</h4>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {regenerationProgress.categories.map((cat, idx) => {
                      const isProcessed = regenerationProgress.processedCategories.includes(cat.name);
                      const isCurrent = regenerationProgress.currentCategory === cat.name;
                      const result = regenerationProgress.categoryResults[cat.name];
                      
                      return (
                        <div 
                          key={cat.name}
                          className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${
                            isCurrent 
                              ? 'bg-blue-50 border-blue-300' 
                              : isProcessed 
                                ? 'bg-green-50 border-green-200' 
                                : 'bg-slate-50 border-slate-200'
                          }`}
                        >
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                            isCurrent 
                              ? 'bg-blue-200' 
                              : isProcessed 
                                ? 'bg-green-200' 
                                : 'bg-slate-200'
                          }`}>
                            {isCurrent ? (
                              <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
                            ) : isProcessed ? (
                              <Check className="w-4 h-4 text-green-600" />
                            ) : (
                              <span className="text-xs text-slate-500">{idx + 1}</span>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium truncate ${
                              isCurrent ? 'text-blue-700' : isProcessed ? 'text-green-700' : 'text-slate-600'
                            }`}>
                              {cat.name}
                            </p>
                            <p className="text-xs text-slate-500">{cat.framework}</p>
                          </div>
                          {isProcessed && result && (
                            <div className="flex items-center gap-2 text-xs">
                              <span className="text-green-600">+{result.added}</span>
                              <span className="text-slate-400">|</span>
                              <span className="text-blue-600">↻{result.updated}</span>
                            </div>
                          )}
                          {cat.is_new && !isProcessed && !isCurrent && (
                            <Badge className="bg-yellow-100 text-yellow-700 text-xs">New</Badge>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
            
            {/* Finalizing Phase */}
            {regenerationProgress.phase === 'finalizing' && (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Loader2 className="w-8 h-8 text-green-600 animate-spin" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                  Finalizing...
                </h3>
                <p className="text-slate-600">
                  Updating audit metadata
                </p>
              </div>
            )}
            
            {/* Deduplicating Phase */}
            {regenerationProgress.phase === 'deduplicating' && (
              <div className="text-center py-8" data-testid="regen-dedup-phase">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="w-8 h-8 text-amber-600 animate-pulse" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                  Deduplicating Controls...
                </h3>
                <p className="text-slate-600 mb-2">
                  Pass 1: Within-framework duplicates &middot; Pass 2: Cross-framework matching
                </p>
                <p className="text-2xl font-mono font-bold text-amber-600">
                  {regenerationProgress.dedupElapsed}s
                </p>
              </div>
            )}
            
            {/* Complete Phase */}
            {regenerationProgress.phase === 'complete' && (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-green-700 mb-2">
                  Regeneration Complete!
                </h3>
                <div className="flex justify-center gap-8 mt-4">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-green-600">{regenerationProgress.totalAdded}</p>
                    <p className="text-sm text-slate-600">Controls Added</p>
                  </div>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-blue-600">{regenerationProgress.totalUpdated}</p>
                    <p className="text-sm text-slate-600">Controls Updated</p>
                  </div>
                </div>
                <p className="text-slate-500 mt-4 text-sm">
                  Refreshing audit data...
                </p>
                {regenerationPromptVersions && Object.keys(regenerationPromptVersions).length > 0 && (
                  <div className="flex flex-wrap gap-1.5 justify-center mt-3">
                    {Object.entries(regenerationPromptVersions).map(([framework, version]) => (
                      <Badge key={framework} variant="outline" className="bg-white font-mono text-xs">
                        {framework}: {version}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            {/* Error Phase */}
            {regenerationProgress.phase === 'error' && (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <AlertTriangle className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-red-700 mb-2">
                  Regeneration Failed
                </h3>
                <p className="text-slate-600 mb-4">
                  An error occurred during regeneration. Please try again.
                </p>
                <Button
                  onClick={handleRegenerateControls}
                  variant="outline"
                  className="border-red-300 text-red-700"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Retry
                </Button>
              </div>
            )}
          </div>
          
          <DialogFooter className="border-t pt-4">
            <Button
              variant="outline"
              onClick={closeRegenerateDialog}
              disabled={regenerating && regenerationProgress.phase !== 'complete' && regenerationProgress.phase !== 'error'}
            >
              {regenerationProgress.phase === 'complete' ? 'Close' : 'Cancel'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Manual Control Dialog */}
      <BulkImportControlsDialog
        open={showBulkImportDialog}
        onOpenChange={setShowBulkImportDialog}
        auditId={auditId}
        auditName={audit?.name || 'audit'}
        existingControls={controls}
        auditFrameworks={audit?.frameworks || []}
        auditCategories={audit?.categories || []}
        auditTechnologies={(audit?.technologies || []).map((t) => (typeof t === 'string' ? (technologyMap[t]?.name || t) : t.name)).filter(Boolean)}
        onImportComplete={fetchAuditData}
      />
      <Dialog open={showAddControlDialog} onOpenChange={setShowAddControlDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-navy-600" />
              Add Control Manually
            </DialogTitle>
            <DialogDescription>
              Add a custom control to this audit. All fields marked with * are required.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Framework */}
            <div className="space-y-2">
              <Label htmlFor="framework">Framework *</Label>
              <Select
                value={controlForm.framework}
                onValueChange={(value) => setControlForm({ ...controlForm, framework: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select framework" />
                </SelectTrigger>
                <SelectContent>
                  {audit.frameworks.map((framework) => (
                    <SelectItem key={framework} value={framework}>
                      {framework}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Control Name */}
            <div className="space-y-2">
              <Label htmlFor="control_name">Control Name *</Label>
              <Input
                id="control_name"
                placeholder="e.g., Access Control Management"
                value={controlForm.control_name}
                onChange={(e) => setControlForm({ ...controlForm, control_name: e.target.value })}
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                placeholder="Describe the control requirements and implementation"
                value={controlForm.description}
                onChange={(e) => setControlForm({ ...controlForm, description: e.target.value })}
                rows={4}
              />
            </div>

            {/* Category */}
            <div className="space-y-2">
              <Label htmlFor="category">Category *</Label>
              <Select
                value={controlForm.category}
                onValueChange={(value) => setControlForm({ ...controlForm, category: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Technical">Technical</SelectItem>
                  <SelectItem value="Administrative">Administrative</SelectItem>
                  <SelectItem value="Physical">Physical</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Evidence Required */}
            <div className="space-y-2">
              <Label htmlFor="evidence_required">Evidence Required</Label>
              <Textarea
                id="evidence_required"
                placeholder="Enter evidence items separated by commas (e.g., Policy documents, Access logs, Screenshots)"
                value={controlForm.evidence_required}
                onChange={(e) => setControlForm({ ...controlForm, evidence_required: e.target.value })}
                rows={3}
              />
              <p className="text-xs text-slate-500">Separate multiple items with commas</p>
            </div>

            {/* Type */}
            <div className="space-y-2">
              <Label htmlFor="type">Type</Label>
              <Select
                value={controlForm.type}
                onValueChange={(value) => setControlForm({ ...controlForm, type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">Manual</SelectItem>
                  <SelectItem value="automated">Auto</SelectItem>
                  <SelectItem value="preventive">Preventive</SelectItem>
                  <SelectItem value="detective">Detective</SelectItem>
                  <SelectItem value="corrective">Corrective</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowAddControlDialog(false);
                setControlForm({
                  framework: '',
                  control_name: '',
                  description: '',
                  category: '',
                  evidence_required: '',
                  type: 'manual'
                });
              }}
              disabled={addingControl}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddManualControl}
              disabled={addingControl}
              className="bg-navy-600 hover:bg-navy-700"
            >
              {addingControl ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Control
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Control Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={(open) => {
        setShowDeleteConfirm(open);
        if (!open) {
          setControlToDelete(null);
          setDeleteConfirmText('');
        }
      }}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              <span>Delete Control</span>
            </DialogTitle>
            <DialogDescription>
              This will move the control to deleted state. You can restore it later if needed.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-700 font-medium mb-2">
                You are about to delete:
              </p>
              <p className="text-sm text-red-900 font-semibold">
                {controlToDelete?.control_name}
              </p>
              <p className="text-xs text-red-600 mt-1">
                Framework: {controlToDelete?.framework} | Category: {controlToDelete?.category}
              </p>
            </div>
            
            <div>
              <Label className="text-sm font-medium text-slate-700">
                Type <span className="font-bold text-red-600">DELETE</span> to confirm
              </Label>
              <Input
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value.toUpperCase())}
                placeholder="Type DELETE to confirm"
                className="mt-2"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowDeleteConfirm(false);
                setControlToDelete(null);
                setDeleteConfirmText('');
              }}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={handleDeleteControl}
              disabled={deleteConfirmText !== 'DELETE' || deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Control
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Optimize with AI Dialog */}
      <Dialog open={showOptimizeDialog} onOpenChange={closeOptimizeDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-purple-600" />
              Optimize Controls with AI
            </DialogTitle>
            <DialogDescription>
              AI will analyze your controls and suggest which ones to remove to reduce redundancy while maintaining compliance coverage.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto py-4">
            {/* Initial State - Show analyze button */}
            {!optimizing && !optimizationResult && (
              <div className="text-center py-8">
                <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Sparkles className="w-10 h-10 text-purple-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                  Ready to Optimize Your Audit
                </h3>
                <p className="text-slate-600 max-w-md mx-auto mb-6">
                  Our AI will analyze {controls.length} controls across your audit frameworks 
                  ({audit?.frameworks?.join(', ')}) and suggest optimizations to reduce 
                  redundancy while maintaining full compliance coverage.
                </p>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 max-w-md mx-auto mb-6">
                  <h4 className="font-medium text-slate-900 mb-2 flex items-center gap-2">
                    <Lightbulb className="w-4 h-4 text-yellow-500" />
                    What AI will look for:
                  </h4>
                  <ul className="text-sm text-slate-600 text-left space-y-1">
                    <li>• Duplicate or redundant controls</li>
                    <li>• Overlapping requirements between frameworks</li>
                    <li>• Overly specific controls covered by broader ones</li>
                    <li>• Similar manual controls that could be consolidated</li>
                  </ul>
                </div>
                <Button
                  onClick={handleOptimizeWithAI}
                  className="bg-purple-600 hover:bg-purple-700"
                  data-testid="start-ai-analysis-btn"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Start AI Analysis
                </Button>
              </div>
            )}

            {/* Loading State - With Progress Tracking */}
            {optimizing && (
              <div className="py-6">
                {/* Progress Header */}
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900">
                    {optimizationProgress.phase === 'loading_categories' 
                      ? 'Loading Categories...' 
                      : 'Analyzing Controls by Category'}
                  </h3>
                </div>
                
                {/* Progress Bar */}
                {optimizationProgress.phase === 'analyzing' && optimizationProgress.totalCategories > 0 && (
                  <div className="max-w-lg mx-auto mb-6">
                    <div className="flex items-center justify-between text-sm text-slate-600 mb-2">
                      <span>Progress</span>
                      <span>{optimizationProgress.processedCategories.length} of {optimizationProgress.totalCategories} categories</span>
                    </div>
                    <Progress 
                      value={(optimizationProgress.processedCategories.length / optimizationProgress.totalCategories) * 100} 
                      className="h-3"
                    />
                  </div>
                )}
                
                {/* Current Category Being Analyzed */}
                {optimizationProgress.currentCategory && (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 max-w-lg mx-auto mb-6">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                        <FolderOpen className="w-5 h-5 text-purple-600" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-purple-600 font-medium">Currently Analyzing</p>
                        <p className="text-slate-900 font-semibold">{optimizationProgress.currentCategory}</p>
                      </div>
                      <Loader2 className="w-5 h-5 text-purple-600 animate-spin" />
                    </div>
                  </div>
                )}
                
                {/* Categories Progress List */}
                {optimizationProgress.categories.length > 0 && (
                  <div className="max-w-lg mx-auto">
                    <h4 className="text-sm font-medium text-slate-700 mb-3">Category Analysis Progress</h4>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {optimizationProgress.categories.map((cat, idx) => {
                        const isProcessed = optimizationProgress.processedCategories.includes(cat.name);
                        const isCurrent = optimizationProgress.currentCategory === cat.name;
                        const result = optimizationProgress.categoryResults[cat.name];
                        
                        return (
                          <div 
                            key={cat.name}
                            className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${
                              isCurrent 
                                ? 'bg-purple-50 border-purple-300' 
                                : isProcessed 
                                  ? 'bg-green-50 border-green-200' 
                                  : 'bg-slate-50 border-slate-200'
                            }`}
                          >
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                              isCurrent 
                                ? 'bg-purple-200' 
                                : isProcessed 
                                  ? 'bg-green-200' 
                                  : 'bg-slate-200'
                            }`}>
                              {isCurrent ? (
                                <Loader2 className="w-4 h-4 text-purple-600 animate-spin" />
                              ) : isProcessed ? (
                                <Check className="w-4 h-4 text-green-600" />
                              ) : (
                                <span className="text-xs text-slate-500">{idx + 1}</span>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm font-medium truncate ${
                                isCurrent ? 'text-purple-700' : isProcessed ? 'text-green-700' : 'text-slate-600'
                              }`}>
                                {cat.name}
                              </p>
                              <p className="text-xs text-slate-500">{cat.count} controls</p>
                            </div>
                            {isProcessed && result && (
                              <div className="flex items-center gap-2 text-xs">
                                <span className="text-green-600">{result.keep?.length || 0} keep</span>
                                <span className="text-slate-400">|</span>
                                <span className="text-red-600">{result.delete?.length || 0} delete</span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                <p className="text-center text-sm text-slate-500 mt-6">
                  AI is analyzing each category for redundant controls...
                </p>
              </div>
            )}

            {/* Results State */}
            {optimizationResult && (
              <div className="space-y-6">
                {/* Summary Card */}
                <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-xl p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                        <TrendingDown className="w-5 h-5 text-purple-600" />
                        Optimization Analysis Complete
                      </h3>
                      <p className="text-slate-600 mt-1">{optimizationResult.analysis_summary}</p>
                    </div>
                    <Badge className="bg-purple-100 text-purple-700 border-purple-200 text-lg px-3 py-1">
                      {optimizationResult.optimization_score}
                    </Badge>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4 mt-4">
                    <div className="bg-white rounded-lg p-4 text-center border border-slate-200">
                      <p className="text-2xl font-bold text-slate-900">{optimizationResult.total_controls}</p>
                      <p className="text-xs text-slate-600">Total Controls</p>
                    </div>
                    <div className="bg-white rounded-lg p-4 text-center border border-green-200">
                      <p className="text-2xl font-bold text-green-600">{optimizationResult.keep_count}</p>
                      <p className="text-xs text-slate-600">Recommended to Keep</p>
                    </div>
                    <div className="bg-white rounded-lg p-4 text-center border border-red-200">
                      <p className="text-2xl font-bold text-red-600">{optimizationResult.delete_count}</p>
                      <p className="text-xs text-slate-600">Recommended to Delete</p>
                    </div>
                  </div>
                </div>

                {/* Recommendations */}
                {optimizationResult.recommendations?.length > 0 && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <h4 className="font-medium text-yellow-800 flex items-center gap-2 mb-2">
                      <Lightbulb className="w-4 h-4" />
                      AI Recommendations
                    </h4>
                    <ul className="text-sm text-yellow-700 space-y-1">
                      {optimizationResult.recommendations.map((rec, idx) => (
                        <li key={idx}>• {rec}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Controls to Delete Section */}
                {optimizationResult.delete?.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-slate-900 flex items-center gap-2">
                        <Trash2 className="w-4 h-4 text-red-500" />
                        Controls Recommended for Deletion ({optimizationResult.delete.length})
                      </h4>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={selectAllForDeletion}
                          className="text-xs"
                        >
                          Select All
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={deselectAllForDeletion}
                          className="text-xs"
                        >
                          Deselect All
                        </Button>
                      </div>
                    </div>
                    <div className="space-y-2 max-h-64 overflow-y-auto border rounded-lg p-2 bg-red-50/50">
                      {optimizationResult.delete.map((control) => (
                        <div
                          key={control.id}
                          className={`flex items-start gap-3 p-3 rounded-lg border transition-all cursor-pointer ${
                            selectedForDeletion.has(control.id)
                              ? 'bg-red-100 border-red-300'
                              : 'bg-white border-slate-200 hover:border-red-200'
                          }`}
                          onClick={() => toggleControlSelection(control.id)}
                          data-testid={`delete-control-${control.id}`}
                        >
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                            selectedForDeletion.has(control.id)
                              ? 'bg-red-500 border-red-500'
                              : 'border-slate-300'
                          }`}>
                            {selectedForDeletion.has(control.id) && (
                              <Check className="w-3 h-3 text-white" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium text-slate-900 text-sm truncate">
                                {control.control_name}
                              </span>
                              <Badge variant="outline" className={`${getFrameworkColor(control.framework)} text-xs`}>
                                {control.framework}
                              </Badge>
                            </div>
                            <p className="text-xs text-red-600 font-medium">
                              Reason: {control.optimization_reason}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Controls to Keep Section */}
                {optimizationResult.keep?.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-slate-900 flex items-center gap-2 mb-3">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                      Controls to Keep ({optimizationResult.keep.length})
                    </h4>
                    <div className="space-y-2 max-h-48 overflow-y-auto border rounded-lg p-2 bg-green-50/50">
                      {optimizationResult.keep.map((control) => (
                        <div
                          key={control.id}
                          className="flex items-start gap-3 p-3 rounded-lg bg-white border border-green-200"
                        >
                          <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <Check className="w-3 h-3 text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium text-slate-900 text-sm truncate">
                                {control.control_name}
                              </span>
                              <Badge variant="outline" className={`${getFrameworkColor(control.framework)} text-xs`}>
                                {control.framework}
                              </Badge>
                            </div>
                            <p className="text-xs text-green-600 font-medium">
                              Reason: {control.optimization_reason}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* No deletions suggested */}
                {optimizationResult.delete?.length === 0 && (
                  <div className="text-center py-8 bg-green-50 rounded-lg border border-green-200">
                    <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-green-900 mb-2">
                      Your Audit is Already Optimized!
                    </h3>
                    <p className="text-green-700">
                      AI did not find any redundant controls to remove. Your audit is lean and efficient.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter className="border-t pt-4">
            <Button
              variant="outline"
              onClick={closeOptimizeDialog}
              disabled={optimizing || applyingOptimization}
            >
              {optimizationResult ? 'Cancel' : 'Close'}
            </Button>
            
            {optimizationResult && optimizationResult.delete?.length > 0 && (
              <Button
                onClick={handleApplyOptimization}
                disabled={selectedForDeletion.size === 0 || applyingOptimization}
                className="bg-red-600 hover:bg-red-700"
                data-testid="apply-optimization-btn"
              >
                {applyingOptimization ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Applying...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete {selectedForDeletion.size} Control{selectedForDeletion.size !== 1 ? 's' : ''}
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link Policies Dialog */}
      <Dialog open={showPolicyDialog} onOpenChange={setShowPolicyDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Link Policies</DialogTitle>
            <DialogDescription>
              Review and link policies to controls that require them. New policies will be created with AI-generated content.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            {!policyStatus ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-5 h-5 animate-spin text-slate-400 mr-2" />
                <span className="text-slate-500 text-sm">Loading policy status...</span>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div 
                    className={`rounded-lg p-3 cursor-pointer transition-all border-2 ${policyTab === 'linked' ? 'bg-green-100 border-green-500 ring-1 ring-green-300' : 'bg-green-50 border-green-200 hover:border-green-400'}`}
                    onClick={() => setPolicyTab(policyTab === 'linked' ? null : 'linked')}
                    data-testid="policy-tab-linked"
                  >
                    <div className="text-xl font-bold text-green-700">{policyStatus.linked_count || 0}</div>
                    <div className="text-xs text-green-600">Linked</div>
                  </div>
                  <div 
                    className={`rounded-lg p-3 cursor-pointer transition-all border-2 ${policyTab === 'needs' ? 'bg-amber-100 border-amber-500 ring-1 ring-amber-300' : 'bg-amber-50 border-amber-200 hover:border-amber-400'}`}
                    onClick={() => setPolicyTab(policyTab === 'needs' ? null : 'needs')}
                    data-testid="policy-tab-needs"
                  >
                    <div className="text-xl font-bold text-amber-700">{policyStatus.needs_policy_count || 0}</div>
                    <div className="text-xs text-amber-600">Needs Policy</div>
                  </div>
                  <div 
                    className={`rounded-lg p-3 cursor-pointer transition-all border-2 ${policyTab === 'none' ? 'bg-slate-200 border-slate-500 ring-1 ring-slate-300' : 'bg-slate-50 border-slate-200 hover:border-slate-400'}`}
                    onClick={() => setPolicyTab(policyTab === 'none' ? null : 'none')}
                    data-testid="policy-tab-none"
                  >
                    <div className="text-xl font-bold text-slate-600">{policyStatus.no_policy_needed_count || 0}</div>
                    <div className="text-xs text-slate-500">No Policy Needed</div>
                  </div>
                </div>
                <p className="text-xs text-slate-400 text-center">
                  Total: {policyStatus.total_controls} Controls &middot; Click a box to view Controls
                </p>

                {/* Linked controls list */}
                {policyTab === 'linked' && (
                  <div className="space-y-1.5">
                    <h4 className="text-sm font-medium text-green-700">Controls with linked policies ({policyStatus.linked_count})</h4>
                    {policyStatus.linked_controls?.map((c, i) => (
                      <div key={i} className="bg-green-50 rounded px-3 py-2 text-sm space-y-1">
                        <div className="text-slate-700">{c.name}</div>
                        <Badge variant="outline" className="bg-white text-green-700 border-green-300 text-xs">{c.policy_name}</Badge>
                      </div>
                    ))}
                    {(!policyStatus.linked_controls || policyStatus.linked_controls.length === 0) && (
                      <p className="text-sm text-slate-400 italic text-center py-2">No linked controls</p>
                    )}
                  </div>
                )}

                {/* Needs policy controls list */}
                {policyTab === 'needs' && (
                  <div className="space-y-1.5">
                    <h4 className="text-sm font-medium text-amber-700">
                      Controls needing policies ({policyStatus.needs_policy_count}) &middot; {policyStatus.needs_policy?.length} unique to create
                    </h4>
                    {policyStatus.needs_policy_controls?.map((c, i) => (
                      <div key={i} className="bg-amber-50 rounded px-3 py-2 text-sm space-y-1">
                        <div className="text-slate-700">{c.name}</div>
                        <Badge variant="outline" className="bg-white text-amber-700 border-amber-300 text-xs">{c.policy_name}</Badge>
                      </div>
                    ))}
                  </div>
                )}

                {/* No policy needed controls list */}
                {policyTab === 'none' && (
                  <div className="space-y-1.5">
                    <h4 className="text-sm font-medium text-slate-600">Controls without policy requirement ({policyStatus.no_policy_needed_count})</h4>
                    {policyStatus.no_policy_controls?.map((c, i) => (
                      <div key={i} className="bg-slate-50 rounded px-3 py-2 text-sm">
                        <span className="text-slate-700">{c.name}</span>
                        <span className="text-slate-400 ml-2 text-xs">({c.category})</span>
                      </div>
                    ))}
                    {(!policyStatus.no_policy_controls || policyStatus.no_policy_controls.length === 0) && (
                      <p className="text-sm text-slate-400 italic text-center py-2">All controls require a policy</p>
                    )}
                  </div>
                )}

                {linkingPolicies && (
                  <div className="space-y-2">
                    <Progress value={(policyLinkProgress.current / policyLinkProgress.total) * 100} />
                    <p className="text-xs text-slate-500 text-center">
                      Processing {policyLinkProgress.current} of {policyLinkProgress.total}...
                    </p>
                  </div>
                )}

                {(policyStatus.needs_policy_count || 0) === 0 && !policyTab && (
                  <div className="text-center py-4">
                    <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
                    <p className="text-sm text-slate-600">All policies are linked!</p>
                  </div>
                )}
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPolicyDialog(false)}>
              Close
            </Button>
            {policyStatus?.needs_policy?.length > 0 && (
              <Button 
                onClick={handleLinkPolicies} 
                disabled={linkingPolicies}
                className="bg-green-600 hover:bg-green-700"
                data-testid="start-link-policies-btn"
              >
                {linkingPolicies ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Linking...</>
                ) : (
                  <><Link2 className="w-4 h-4 mr-2" /> Link All Policies</>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Control Detail Modal */}
      <Dialog open={Boolean(modalControlId)} onOpenChange={(open) => {
        if (!open) {
          setModalControlId(null);
          fetchAuditData();
        }
      }}>
        <DialogContent
          className="max-w-[96vw] w-[96vw] h-[95vh] p-0 overflow-hidden translate-x-[-50%] translate-y-[-50%] sm:rounded-lg bg-appbg"
          data-testid="control-detail-modal"
        >
          <div className="h-full overflow-y-auto bg-appbg">
            {modalControlId && (
              <ControlDetail
                controlId={modalControlId}
                isModal
                onClose={() => {
                  setModalControlId(null);
                  fetchAuditData();
                }}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Mandatory "Not Applicable" reason prompt */}
      <NaReasonDialog
        open={naDialog.open}
        controlName={naDialog.controlName}
        onCancel={() => setNaDialog({ open: false, controlId: null, controlName: '' })}
        onConfirm={async (reason) => {
          await updateControlStatus(naDialog.controlId, 'not_applicable', reason);
          setNaDialog({ open: false, controlId: null, controlName: '' });
        }}
      />

      {/* Sprint A — Technology lifecycle */}
      <ManageTechnologiesDialog
        open={showManageTechDialog}
        onClose={() => setShowManageTechDialog(false)}
        audit={audit}
        technologyMap={technologyMap}
        allTechnologies={allTechnologies}
        onChanged={fetchAuditData}
      />

      {/* Sprint B — Framework lifecycle */}
      <ManageFrameworksDialog
        open={showManageFwDialog}
        onClose={() => setShowManageFwDialog(false)}
        audit={audit}
        onChanged={fetchAuditData}
      />
    </div>
  );
};

export default AuditDetail;